import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, NavLink, useNavigate, Link, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';
import { readUnauthorizedMessage } from '../utils/authSession';
import { canSeeCrmNavItem } from '../utils/crmPermissions';
import { INITIAL_PENDING_RECETAS, initialState as PEDIDOS_SEED } from '../pages/PedidosRecetasMedico';
import { API_BASE_URL } from '../config/env';
import '../styles/crm.css';
import '../styles/crm-status-badge.css';
import '../styles/crm-sidebar-premium.css';
import '../styles/pulse-genie-dock.css';
import MaintenanceBarrier from '../components/MaintenanceBarrier';
import ChatwootAssignmentToastListener from '../components/ChatwootAssignmentToastListener';
import ChatwootIncomingMessageFab, {
  buildUnreadFabPreviewList,
  enrichFabPreviewListWithProfiles
} from '../components/ChatwootIncomingMessageFab';
import {
  useCrmNavDock,
  CRM_NAV_DOCK_EDGES,
  navDockEdgeStorageKey,
  readNavDockEdgePref,
  readNavDockLeftExpandedFixedPref,
  writeNavDockLeftExpandedFixedPref
} from '../hooks/useCrmNavDock';
import { useCrmNavDockDragPin } from '../hooks/useCrmNavDockDragPin';
import { useDockMagnify } from '../hooks/useDockMagnify';
import { useCrmNavShortcuts } from '../hooks/useCrmNavShortcuts';
import { CrmShortcutsDock, CrmShortcutsToast } from '../components/CrmShortcutsPanel';
import {
  CrmBottomDockLauncherButton,
  CrmBottomDockLauncherPortal
} from '../components/CrmBottomDockLauncher';
import { MAX_BOTTOM_DOCK_PINS, fireBottomDockLimitAlert } from '../config/crmNavDock';
import {
  shouldScopePedidosToMedicoUser,
  resolveLinkedMedicoIdFromUser,
  countPedidosForMedicoUser
} from '../utils/medicoPedidosScope';
import { PEDIDO_DERIVADO_AL_MEDICO_EVENT } from '../utils/pedidosRecetaMedicoSync';
import { loadAtencionPedidosForFarmacia } from '../api/atencionPedidos';
import PulseCommandPalette, { PulseCommandDock } from '../components/PulseCommandPalette';
import { PulseAboutDock } from '../components/PulseAboutDock';
import ArgentinaMundialWelcome, { useArgentinaMundialWelcome } from '../components/ArgentinaMundialWelcome';
import { consumeMundialWelcomePending } from '../config/mundialWelcome';
import { usePulseCommand } from '../hooks/usePulseCommand';
import { exitKioskMode, isKioskModeActive, toggleKioskMode } from '../utils/kioskMode';
import '../styles/pulse-command-premium.css';
import '../styles/pulse-about-premium.css';

const RESERVED_N8N_USER = {
  username: 'admin',
  correo: 'admin@gmail.com'
};

function formatProfileDoctorName(nombre) {
  const raw = String(nombre || '').trim();
  if (!raw) return '';
  if (/^(dr|dra)\.?\s/i.test(raw)) return raw;
  return `Dr/a. ${raw}`;
}

function resolveProfileLinkedCatalogs(profileData, sessionUser) {
  const items = [];
  const adminName = String(
    profileData?.administrativo_nombre || sessionUser?.administrativo_nombre || ''
  ).trim();
  const medicoName = String(
    profileData?.medico_nombre || sessionUser?.medico_nombre || ''
  ).trim();
  const callcenterName = String(
    profileData?.callcenter_nombre || sessionUser?.callcenter_nombre || ''
  ).trim();

  if (adminName) {
    items.push({
      kind: 'administrativo',
      roleLabel: 'Administrativo',
      name: adminName,
      icon: '👤'
    });
  }
  if (medicoName) {
    items.push({
      kind: 'medico',
      roleLabel: 'Médico',
      name: formatProfileDoctorName(medicoName),
      icon: '🧑‍⚕️'
    });
  }
  if (callcenterName) {
    items.push({
      kind: 'callcenter',
      roleLabel: 'Call Center',
      name: callcenterName,
      icon: '🎧'
    });
  }
  return items;
}

function resolveLayoutPersonDisplayName(profileData, sessionUser) {
  const adminName = String(
    profileData?.administrativo_nombre || sessionUser?.administrativo_nombre || ''
  ).trim();
  const medicoName = String(
    profileData?.medico_nombre || sessionUser?.medico_nombre || ''
  ).trim();
  const callcenterName = String(
    profileData?.callcenter_nombre || sessionUser?.callcenter_nombre || ''
  ).trim();

  if (adminName) return adminName;
  if (medicoName) return formatProfileDoctorName(medicoName);
  if (callcenterName) return callcenterName;

  const rawName = String(sessionUser?.name || sessionUser?.username || '').trim();
  return rawName || 'Usuario';
}

function resolveLayoutPersonDisplayInitial(profileData, sessionUser) {
  const displayName = resolveLayoutPersonDisplayName(profileData, sessionUser);
  const normalized = displayName.replace(/^(dr\/a\.|dr\.|dra\.)\s*/i, '').trim();
  const letter = normalized.charAt(0);
  if (letter) return letter.toUpperCase();
  const username = String(sessionUser?.username || '').trim();
  return username ? username.charAt(0).toUpperCase() : '?';
}

function resolveLayoutPersonRoleSubtitle(linkedCatalogs) {
  if (!Array.isArray(linkedCatalogs) || linkedCatalogs.length === 0) {
    return 'Gestión Asociativa';
  }
  if (linkedCatalogs.length === 1) {
    return linkedCatalogs[0].roleLabel;
  }
  return linkedCatalogs.map((item) => item.roleLabel).join(' · ');
}

function isReservedForN8nProfile(profile, sessionUser) {
  const username = String(profile?.username || sessionUser?.username || '').trim().toLowerCase();
  const correo = String(profile?.correo || sessionUser?.correo || sessionUser?.email || '').trim().toLowerCase();
  return username === RESERVED_N8N_USER.username && correo === RESERVED_N8N_USER.correo;
}

function buildLeftDockPinnedSet(rawPaths, allPaths) {
  const allowed = new Set(allPaths);
  if (rawPaths == null) return new Set(allPaths);
  if (!Array.isArray(rawPaths)) return new Set(allPaths);
  const valid = rawPaths
    .map((path) => String(path || '').trim())
    .filter((path) => allowed.has(path));
  return valid.length > 0 ? new Set(valid) : new Set(allPaths);
}

function buildLeftDockPinnedSetFromPins(rawPins, allPaths) {
  const allowed = new Set(allPaths);
  if (!Array.isArray(rawPins) || !rawPins.length) return new Set(allPaths);
  const visible = rawPins
    .filter((row) => {
      const path = String(row?.path || row?.nav_path || '').trim();
      return allowed.has(path) && (row?.is_visible === true || row?.is_visible === 'true' || row?.is_visible === 1);
    })
    .map((row) => String(row?.path || row?.nav_path || '').trim());
  return visible.length > 0 ? new Set(visible) : new Set(allPaths);
}

function buildLeftDockPinsPayload(pathsSet, navItems) {
  return navItems.map((item) => ({
    path: item.to,
    is_visible: pathsSet.has(item.to)
  }));
}

const CHATWOOT_CONVERSATIONS_CHANGED_EVENT = 'pulse:chatwoot-conversations-changed';

function chatwootConversationsFingerprint(list) {
  if (!Array.isArray(list) || !list.length) return '';
  return list
    .map((c) =>
      [c.id, c.updated_at, c.unread_count, c.last_message?.id].join(':')
    )
    .join('|');
}

function readLeftDockPinnedPaths(storageKey, allPaths) {
  if (typeof window === 'undefined') return new Set(allPaths);
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return new Set(allPaths);
    const parsed = JSON.parse(raw);
    return buildLeftDockPinnedSet(parsed, allPaths);
  } catch {
    return new Set(allPaths);
  }
}

function writeLeftDockPinnedPaths(storageKey, pathsSet) {
  try {
    localStorage.setItem(storageKey, JSON.stringify([...pathsSet]));
  } catch {
    // ignore quota / private mode
  }
}

const IconSearch = () => (
  <svg className="crm-nav-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </svg>
);

const IconChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const IconMonitor = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

const IconChevronRightSmall = () => (
  <svg className="crm-u-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const IconMenu = () => (
  <svg className="crm-mobile-menu-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);

const IconClose = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

function NavDockDragHandle({ onStartDrag, className = '' }) {
  return (
    <button
      type="button"
      className={`crm-nav-dock__handle ${className}`.trim()}
      aria-label="Arrastrar barra de navegación. Soltá cerca de un borde para anclar."
      title="Arrastrá para mover la barra (izquierda o abajo)"
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        onStartDrag(event.clientX, event.clientY);
      }}
      onPointerUp={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      }}
    >
      <span className="crm-nav-dock__handle-grip" aria-hidden="true" />
    </button>
  );
}

function ProfileDrawerLoading() {
  return (
    <div className="crm-profile-loading" role="status" aria-live="polite" aria-busy="true">
      <div className="crm-profile-loading-status">
        <span className="crm-profile-loading-spinner" aria-hidden="true" />
        <div className="crm-profile-loading-copy">
          <div className="crm-profile-loading-title">Cargando tu perfil</div>
          <div className="crm-profile-loading-hint">Usuario, email y teléfono…</div>
        </div>
      </div>
      <div className="crm-profile-info-card crm-profile-info-card--skeleton" aria-hidden="true">
        <div className="crm-profile-info-card-head">
          <div className="crm-profile-info-card-title">📇 Datos de contacto</div>
          <div className="crm-profile-info-rows-inline">
            <div className="crm-profile-loading-row" />
            <div className="crm-profile-loading-row" />
            <div className="crm-profile-loading-row" />
          </div>
        </div>
      </div>
    </div>
  );
}

function NavSearchField({
  value,
  onChange,
  inputName,
  className = 'crm-nav-search-desktop',
  placeholder = 'Buscar módulo… (Ctrl+K global)',
  onFocus
}) {
  return (
    <div className={`crm-nav-search-wrap ${className}`}>
      <IconSearch />
      <input
        type="text"
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        placeholder={placeholder}
        autoComplete="off"
        name={inputName}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        className="crm-nav-search-input"
      />
      {String(value || '').trim() ? (
        <button
          type="button"
          className="crm-nav-search-clear"
          onClick={() => onChange({ target: { value: '' } })}
          aria-label="Limpiar búsqueda"
        >
          ×
        </button>
      ) : null}
    </div>
  );
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function imageFromDataUrl(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

async function optimizeProfileImage(dataUrl) {
  const img = await imageFromDataUrl(dataUrl);
  const maxSide = 1280;
  const scale = Math.min(1, maxSide / Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height));
  const targetW = Math.max(1, Math.round((img.naturalWidth || img.width) * scale));
  const targetH = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));
  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, targetW, targetH);
  return canvas.toDataURL('image/jpeg', 0.82);
}

function PrivateLayout() {
  const { user, logout, reportUnauthorized } = useAuth();
  const mundialWelcome = useArgentinaMundialWelcome(user);
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadFabItems, setUnreadFabItems] = useState([]);
  const chatwootListFingerprintRef = useRef('');
  const chatwootConversationsRef = useRef([]);
  const [pendingPedidosCount, setPendingPedidosCount] = useState(0);
  const [linkedMedicoIdForBadge, setLinkedMedicoIdForBadge] = useState(() => resolveLinkedMedicoIdFromUser(user));
  const pedidosBadgeScopedToMedico = useMemo(() => shouldScopePedidosToMedicoUser(user), [user]);
  const [pendingDerivacionCount, setPendingDerivacionCount] = useState(0);
  const [pendingRecetasCount, setPendingRecetasCount] = useState(() => INITIAL_PENDING_RECETAS || 0);
  const [pendingDispensaCount, setPendingDispensaCount] = useState(() => (Array.isArray(PEDIDOS_SEED.pedidos_farmacia) ? PEDIDOS_SEED.pedidos_farmacia.filter(p => ['pendiente_call_center', 'pedido_generado_farmacia', 'en_preparacion'].includes(String(p.estado))).length : 0));
  const [enCaminoPedidosCount, setEnCaminoPedidosCount] = useState(() => (Array.isArray(PEDIDOS_SEED.pedidos_farmacia) ? PEDIDOS_SEED.pedidos_farmacia.filter(p => p.estado === 'en_camino').length : 0));
  const [isKiosk, setIsKiosk] = useState(false);
  const [isAboutPanelOpen, setIsAboutPanelOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [navSearch, setNavSearch] = useState('');
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);
  const [profileData, setProfileData] = useState({
    username: user?.username || '',
    correo: '',
    telefono: '',
    medico_id: null,
    medico_nombre: '',
    medico_especialidad: '',
    administrativo_id: null,
    administrativo_nombre: ''
  });
  const [profilePhoto, setProfilePhoto] = useState('');
  const [profilePhotoOriginal, setProfilePhotoOriginal] = useState('');
  const [savedPhotoPreset, setSavedPhotoPreset] = useState('natural');
  const [isPhotoStudioOpen, setIsPhotoStudioOpen] = useState(false);
  const [photoStylePreset, setPhotoStylePreset] = useState('natural');
  const [isApplyingPhotoStyle, setIsApplyingPhotoStyle] = useState(false);
  const [photoStudioBasePhoto, setPhotoStudioBasePhoto] = useState('');
  const [isComparingPhoto, setIsComparingPhoto] = useState(false);
  const [photoZoom, setPhotoZoom] = useState(1);
  const [photoPan, setPhotoPan] = useState({ x: 0, y: 0 });
  const [isPhotoDragging, setIsPhotoDragging] = useState(false);
  const [photoFlashTick, setPhotoFlashTick] = useState(0);
  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const photoPreviewRef = useRef(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordChangeInlineFeedback, setPasswordChangeInlineFeedback] = useState({ type: '', text: '' });
  const [floatingPasswordNote, setFloatingPasswordNote] = useState(null);
  const [isPasswordSectionOpen, setIsPasswordSectionOpen] = useState(false);
  const [isProfileExtrasOpen, setIsProfileExtrasOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [isCameraReady, setIsCameraReady] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const [isLeftDockExpandedFixed, setIsLeftDockExpandedFixed] = useState(() =>
    readNavDockLeftExpandedFixedPref(user?.username)
  );
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return true;
    return !readNavDockLeftExpandedFixedPref(user?.username);
  });
  const [navDockEdgePref, setNavDockEdgePref] = useState(() => readNavDockEdgePref(user?.username));
  const [isDesktopSidebar, setIsDesktopSidebar] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 769px)').matches;
  });
  const sidebarCollapsed = isDesktopSidebar && isSidebarCollapsed;
  const isFloatingNavDock = sidebarCollapsed && isDesktopSidebar;

  const persistNavDockEdge = useCallback(async (edge) => {
    const storageKey = navDockEdgeStorageKey(user?.username);
    try {
      localStorage.setItem(storageKey, edge);
    } catch (_) {
      // ignore storage errors
    }
    if (!user?.id) return;
    try {
      await fetch(`${API_BASE_URL}/api/auth/nav-dock`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ edge })
      });
    } catch (_) {
      // silent persist failure
    }
  }, [user?.id, user?.username]);

  const navDock = useCrmNavDock({
    enabled: isFloatingNavDock,
    initialEdge: navDockEdgePref,
    onEdgeChange: persistNavDockEdge
  });
  const isSideNavDock = isFloatingNavDock && navDock.dockEdge === 'left';
  const isBottomNavDock = isFloatingNavDock && navDock.dockEdge === 'bottom';
  const isLeftNavEdge = isDesktopSidebar && navDock.dockEdge === 'left';
  const isLeftPremiumExpanded = isLeftNavEdge && !sidebarCollapsed;

  const persistLeftDockExpandedFixed = useCallback(async (fixed) => {
    writeNavDockLeftExpandedFixedPref(user?.username, fixed);
    if (!user?.id) return;
    try {
      await fetch(`${API_BASE_URL}/api/auth/nav-dock-left-fixed`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fixed })
      });
    } catch (_) {
      // silent persist failure
    }
  }, [user?.id, user?.username]);

  const handleToggleLeftDockExpandedFixed = useCallback(() => {
    setIsLeftDockExpandedFixed((prev) => {
      const next = !prev;
      persistLeftDockExpandedFixed(next);
      return next;
    });
  }, [persistLeftDockExpandedFixed]);

  const handleToggleSidebarCollapsed = useCallback(() => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      if (next) {
        setIsLeftDockExpandedFixed(false);
        persistLeftDockExpandedFixed(false);
      }
      return next;
    });
  }, [persistLeftDockExpandedFixed]);

  const buildStrongPassword = () => {
    const lowers = 'abcdefghijkmnopqrstuvwxyz';
    const uppers = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const numbers = '23456789';
    const symbols = '!@#$%&*_-+=?';
    const allChars = `${lowers}${uppers}${numbers}${symbols}`;
    const pick = (pool) => pool[Math.floor(Math.random() * pool.length)];
    const seed = [pick(lowers), pick(uppers), pick(numbers), pick(symbols)];
    while (seed.length < 14) seed.push(pick(allChars));
    for (let i = seed.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [seed[i], seed[j]] = [seed[j], seed[i]];
    }
    return seed.join('');
  };

  useEffect(() => {
    let isMounted = true;

    async function fetchUnreadConversations() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/chatwoot/conversations?status=all&page=1`, {
          credentials: 'include'
        });
        if (!res.ok) return;
        const json = await res.json();
        const list = Array.isArray(json?.data) ? json.data : [];
        chatwootConversationsRef.current = list;
        const listFingerprint = chatwootConversationsFingerprint(list);
        if (listFingerprint !== chatwootListFingerprintRef.current) {
          chatwootListFingerprintRef.current = listFingerprint;
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(CHATWOOT_CONVERSATIONS_CHANGED_EVENT));
          }
        }
        const totalUnread = list.reduce((acc, conv) => acc + Number(conv.unread_count || 0), 0);
        const items = buildUnreadFabPreviewList(list);
        if (isMounted) {
          setUnreadCount(totalUnread);
          setUnreadFabItems(items);
        }

        if (isMounted && items.length) {
          const enriched = await enrichFabPreviewListWithProfiles(items, list);
          if (isMounted) setUnreadFabItems(enriched);
        }
      } catch {
        // No bloqueamos UI del sidebar por fallos de red.
      }
    }

    fetchUnreadConversations();
    const intervalId = setInterval(fetchUnreadConversations, 2500);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!pedidosBadgeScopedToMedico) {
      setLinkedMedicoIdForBadge(null);
      return undefined;
    }
    const fromUser = resolveLinkedMedicoIdFromUser(user);
    if (fromUser) {
      setLinkedMedicoIdForBadge(fromUser);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/profile`, { credentials: 'include' });
        if (!res.ok || cancelled) return;
        const profile = await res.json();
        if (!cancelled) setLinkedMedicoIdForBadge(resolveLinkedMedicoIdFromUser(profile));
      } catch (_) {
        if (!cancelled) setLinkedMedicoIdForBadge(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, pedidosBadgeScopedToMedico]);

  useEffect(() => {
    let isMounted = true;

    async function fetchPendingPedidos() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos-pendientes`, { credentials: 'include' });
        if (!res.ok) return;
        const json = await res.json();
        const list = Array.isArray(json) ? json : (Array.isArray(json?.pedidos) ? json.pedidos : []);
        const count = pedidosBadgeScopedToMedico
          ? countPedidosForMedicoUser(list, user, linkedMedicoIdForBadge)
          : (list.length || 0);
        if (isMounted) setPendingPedidosCount(count);
      } catch {
        // no bloqueamos la UI por fallos de red
      }
    }

    fetchPendingPedidos();
    const pid = setInterval(fetchPendingPedidos, 5000);
    const onDerivado = () => {
      void fetchPendingPedidos();
    };
    window.addEventListener(PEDIDO_DERIVADO_AL_MEDICO_EVENT, onDerivado);
    return () => {
      isMounted = false;
      clearInterval(pid);
      window.removeEventListener(PEDIDO_DERIVADO_AL_MEDICO_EVENT, onDerivado);
    };
  }, [user, pedidosBadgeScopedToMedico, linkedMedicoIdForBadge]);

  useEffect(() => {
    let isMounted = true;

    async function fetchPendingDerivacion() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos-derivacion`, { credentials: 'include' });
        if (!res.ok) return;
        const json = await res.json();
        const list = Array.isArray(json) ? json : (Array.isArray(json?.pedidos) ? json.pedidos : []);
        if (isMounted) setPendingDerivacionCount(list.length || 0);
      } catch {
        // no bloqueamos la UI por fallos de red
      }
    }

    fetchPendingDerivacion();
    const did = setInterval(fetchPendingDerivacion, 5000);
    return () => { isMounted = false; clearInterval(did); };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function fetchPendingDispensa() {
      try {
        const [classicJson, atencionRows] = await Promise.all([
          fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos_farmacia`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
          loadAtencionPedidosForFarmacia({ limit: 150 }).catch(() => [])
        ]);

        const classicList = Array.isArray(classicJson)
          ? classicJson
          : (Array.isArray(classicJson?.pedidos) ? classicJson.pedidos : []);
        const atencionList = Array.isArray(atencionRows) ? atencionRows : [];

        // Mergeamos clásicos + bot mostrador deduplicando por id (igual que contact-center)
        const byId = new Map();
        for (const p of classicList) { if (p) byId.set(String(p.id), p); }
        for (const p of atencionList) { if (p && !byId.has(String(p.id))) byId.set(String(p.id), p); }
        const all = Array.from(byId.values());

        if (isMounted) {
          const pendingCount = all.filter((p) => ['pendiente_call_center', 'pedido_generado_farmacia', 'en_preparacion'].includes(String(p.estado))).length;
          const enCaminoCount = all.filter((p) => String(p.estado) === 'en_camino').length;
          setPendingDispensaCount(pendingCount);
          setEnCaminoPedidosCount(enCaminoCount);
        }
      } catch (e) {
        // ignore network errors
      }
    }

    fetchPendingDispensa();
    const pid = setInterval(fetchPendingDispensa, 5000);
    return () => { isMounted = false; clearInterval(pid); };
  }, []);

  useEffect(() => {
    let isMounted = true;
    let rid = null;

    async function fetchPendingRecetas() {
      try {
        const rangeStart = new Date();
        rangeStart.setHours(0, 0, 0, 0);
        rangeStart.setDate(rangeStart.getDate() - 7);
        const rangeEnd = new Date();
        rangeEnd.setHours(0, 0, 0, 0);
        rangeEnd.setDate(rangeEnd.getDate() + 7);

        const isInEmitWindow = (dateLike) => {
          if (!dateLike) return false;
          const d = new Date(`${String(dateLike).slice(0, 10)}T00:00:00`);
          if (Number.isNaN(d.getTime())) return false;
          d.setHours(0, 0, 0, 0);
          return d >= rangeStart && d <= rangeEnd;
        };

        const getPlannedDate = (pedido, recetaNumber) => {
          const rawStart = pedido?.fecha_inicio || pedido?.fecha_solicitud;
          if (!rawStart) return null;
          const base = new Date(rawStart);
          if (Number.isNaN(base.getTime())) return null;
          base.setHours(0, 0, 0, 0);
          const numero = Math.max(1, Number(recetaNumber) || 1);
          base.setDate(base.getDate() + (numero - 1) * 30);
          return base.toISOString().slice(0, 10);
        };

        // Align sidebar badge with RecetasEmitir data source.
        const pedidosRes = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos`, { credentials: 'include' });
        if (!pedidosRes.ok) return true;
        const pedidosJson = await pedidosRes.json();
        const pedidos = Array.isArray(pedidosJson) ? pedidosJson : (Array.isArray(pedidosJson?.pedidos) ? pedidosJson.pedidos : []);
        const elegibles = pedidos.filter(p => p && ['aprobado', 'en_tratamiento'].includes(String(p.estado)));

        let totalPending = 0;
        await Promise.all(elegibles.map(async (p) => {
          const cantidad = Math.max(1, Number(p?.cantidad_recetas) || 1);
          try {
            const recRes = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos/${p.id}/recetas`, { credentials: 'include' });
            if (!recRes.ok) {
              for (let idx = 1; idx <= cantidad; idx += 1) {
                if (isInEmitWindow(getPlannedDate(p, idx))) totalPending += 1;
              }
              return;
            }
            const recJson = await recRes.json().catch(() => ({}));
            const recs = Array.isArray(recJson) ? recJson : (Array.isArray(recJson?.recetas) ? recJson.recetas : []);
            if (!recs.length) {
              for (let idx = 1; idx <= cantidad; idx += 1) {
                if (isInEmitWindow(getPlannedDate(p, idx))) totalPending += 1;
              }
              return;
            }
            const persistedPending = recs.filter((r) => {
              if (String(r?.estado || 'pendiente') !== 'pendiente') return false;
              const persistedDate = r?.fecha_prevista || r?.fechaPrevista || null;
              const fallbackDate = getPlannedDate(p, r?.numero_receta || r?.numero || 1);
              return isInEmitWindow(persistedDate || fallbackDate);
            }).length;
            const remainingNotPersisted = Math.max(cantidad - recs.length, 0);
            let remainingPendingInWindow = 0;
            if (remainingNotPersisted > 0) {
              for (let idx = recs.length + 1; idx <= cantidad; idx += 1) {
                if (isInEmitWindow(getPlannedDate(p, idx))) remainingPendingInWindow += 1;
              }
            }
            totalPending += (persistedPending + remainingPendingInWindow);
          } catch {
            for (let idx = 1; idx <= cantidad; idx += 1) {
              if (isInEmitWindow(getPlannedDate(p, idx))) totalPending += 1;
            }
          }
        }));

        if (isMounted) setPendingRecetasCount(totalPending);
        return true;
      } catch {
        return true;
      }
    }

    (async () => {
      const ok = await fetchPendingRecetas();
      if (!ok) return;
      rid = setInterval(fetchPendingRecetas, 8000);
    })();

    return () => { isMounted = false; if (rid) clearInterval(rid); };
  }, []);

  const navGroups = useMemo(
    () => ([
      {
        key: 'inicio',
        label: 'Inicio',
        icon: '🏠',
        children: [
          { to: '/', label: 'Home', icon: '🏠' }
        ]
      },
      {
        key: 'recetas',
        label: 'Recetas',
        icon: '🔗',
        children: [
          { to: '/directorio', label: '- Directorio -', icon: '🧩', permission: 'ver_directorio' },
          { to: '/gestion-derivacion', label: '1. Pedidos', icon: '📋', unread: pendingDerivacionCount, permission: 'ver_gestion_derivacion' },
          { to: '/pedidos-recetas-medico', label: '2. Aprobación Tratamientos', icon: '🩺', unread: pendingPedidosCount, permission: 'ver_pedido_recetas_medico' },
          { to: '/recetas-emitir', label: '3. Recetas a emitir', icon: '📄', unread: pendingRecetasCount, permission: 'ver_recetas_emitir' }
        ]
      },
      {
        key: 'asociados',
        label: 'Responsables',
        icon: '📨',
        children: [
          { to: '/admin/administrativos-visibilidad', label: 'Administrativo alta', icon: '🧑‍💼', permission: 'admin_administrativos_visibilidad' },
          { to: '/admin/callcenter-visibilidad', label: 'CallCenter alta', icon: '🎧', permission: 'admin_callcenter_visibilidad' },
          { to: '/admin/medicos-visibilidad', label: 'Médico alta', icon: '🧑‍⚕️', permission: 'admin_medicos_visibilidad' },
        ]
      },
      {
        key: 'control',
        label: 'Auditoría y trazabilidad',
        icon: '🎯',
        children: [
          {
            to: '/bitacora-operativa',
            label: 'Bitácora Operativa',
            icon: '◈',
            permission: 'ver_pulse_ledger'
          },
          {
            to: '/gestion-casos',
            label: 'Gestión de Casos',
            icon: '📊',
            permission: 'ver_trazabilidad_asignaciones'
          },
          { to: '/recetas-cruzadas', label: 'Recetas cruzadas', icon: '🔄', permission: 'ver_recetas_cruzadas' },
          { to: '/ruta-recetas', label: 'Ruta de Receta', icon: '🧭', permission: 'ver_trazabilidad_recetas' },
        ]
      },
      {
        key: 'farmacia',
        label: 'Farmacia',
        icon: '💊',
        children: [
          { to: '/promociones-bancarias', label: 'Banco Promos', icon: '🏦', permission: 'ver_promociones_bancarias' },
          { to: '/bot-farmacia', label: 'Bot Farmacia', icon: '🤖', permission: 'ver_bot_farmacia' },
          { to: '/centro-regional', label: 'Centro Regional', icon: '🏥', permission: 'ver_informacion_centro_regional' },
          { to: '/farmacia/horarios-bot', label: 'Chat Horario', icon: '⏰', permission: 'admin_horarios_botrecetasatencion' },
          { to: '/farmacia/delivery-config', label: 'Config Delivery', icon: '💲', permission: 'ver_delivery_config' },
          { to: '/contact-center', label: 'Contact Center', icon: '🎧', unread: pendingDispensaCount, permission: 'ver_pedidos_dispensa' },
          { to: '/delivery', label: 'Delivery', icon: '🏍️', unread: enCaminoPedidosCount, permission: 'ver_pedidos' },
          { to: '/metodo-pago', label: 'Métodos de Pago', icon: '💳', permission: 'ver_metodo_pago' },
          { to: '/bot-farmacia/promociones', label: 'Promociones', icon: '🏷️', permission: 'ver_promociones_bot_farmacia' },
          { to: '/farmacia/poligonos', label: 'Rango Cobertura', icon: '🗺️', permission: 'admin_poligonos_farmacia' },
          { to: '/farmacia/sucursales', label: 'Sucursales', icon: '📍', permission: 'admin_sucursales_farmacia' },
          { to: '/grupo-bienestar-tyc', label: 'TyC GB', icon: '📑', permission: 'ver_terminos_condiciones_grupo_bienestar' },
        ]
      },
      {
        key: 'contacto',
        label: 'CRM',
        icon: '👥',
        children: [
          { to: '/asociacion-clientes', label: 'Contactos', icon: '👥', permission: 'ver_contactos' },
          { to: '/conversaciones', label: 'Conversaciones', icon: '💬', unread: unreadCount, permission: 'ver_conversaciones' },
        ]
      },
      {
        key: 'encuesta',
        label: 'Encuestas',
        icon: '⭐',
        children: [
          { to: '/encuesta-bot', label: 'Encuesta bot', icon: '⭐', permission: 'ver_encuesta_bot' },
          { to: '/encuesta-bot/review', label: 'Review encuestas', icon: '�', permission: 'ver_encuesta_bot_review' }
        ]
      },
      {
        key: 'manual-crm',
        label: 'Manual CRM',
        icon: '◇',
        children: [
          { to: '/admin/manual-usuario', label: 'Manual CRM', icon: '📘', adminOnly: true },
          { to: '/admin/manual-conversaciones', label: 'Manual Chat', icon: '💬', permission: 'manual_conversaciones' },
          { to: '/admin/manual-n8n', label: 'Manual N8N', icon: '⚡', adminOnly: true },
          { to: '/admin/manual-administrativo', label: 'Manual Admin', icon: '📗', permission: 'ver_manual_administrativo' },
          { to: '/admin/manual-medico', label: 'Manual Médico', icon: '📕', permission: 'ver_manual_medico' }
        ]
      },
      {
        key: 'admin',
        label: 'Administración',
        icon: '⚙️',
        children: [
          { to: '/admin/configuracion', label: 'Configuración', icon: '⚙️', adminOnly: true },
          { to: '/admin/usuarios', label: 'Admin usuarios', icon: '🛡️', adminOnly: true },
          { to: '/admin/roles-permisos', label: 'Admin Permisos', icon: '🔐', adminOnly: true }
        ]
      }
    ]),
    [unreadCount, pendingPedidosCount, pendingDerivacionCount, pendingDispensaCount, pendingRecetasCount, enCaminoPedidosCount]
  );

  const canSeeConversaciones = Boolean(
    Array.isArray(user?.permissions) && user.permissions.includes('ver_conversaciones')
  );

  const {
    availableShortcuts: crmNavShortcuts,
    isPanelOpen: isShortcutsPanelOpen,
    openPanel: openShortcutsPanel,
    closePanel: closeShortcutsPanel,
    goToShortcut: goToCrmShortcut,
    flash: shortcutsFlash
  } = useCrmNavShortcuts(user);

  const canSeeNavItem = (it) => canSeeCrmNavItem(user, it);

  const visibleNavGroups = useMemo(() => (
    navGroups
      .map((group) => ({ ...group, children: group.children.filter(canSeeNavItem) }))
      .filter((group) => group.children.length > 0)
  ), [navGroups, user]);

  const flatNavItemsForCommand = useMemo(
    () => visibleNavGroups.flatMap((group) => group.children),
    [visibleNavGroups]
  );

  const getChatwootConversationsForCommand = useCallback(
    () => chatwootConversationsRef.current,
    []
  );

  const pulseCommand = usePulseCommand({
    user,
    navItems: flatNavItemsForCommand,
    shortcuts: crmNavShortcuts,
    getConversations: getChatwootConversationsForCommand,
    unreadCount
  });

  const filteredNavGroups = useMemo(() => {
    const q = String(navSearch || '').trim().toLowerCase();
    if (!q) return visibleNavGroups;
    return visibleNavGroups
      .map((group) => {
        const groupMatch = String(group.label || '').toLowerCase().includes(q);
        const children = groupMatch
          ? group.children
          : group.children.filter((it) => String(it.label || '').toLowerCase().includes(q));
        return { ...group, children };
      })
      .filter((group) => group.children.length > 0);
  }, [navSearch, visibleNavGroups]);

  const totalFilteredNavItems = useMemo(
    () => filteredNavGroups.reduce((acc, g) => acc + g.children.length, 0),
    [filteredNavGroups]
  );
  const flatDockNavItems = useMemo(
    () => filteredNavGroups.flatMap((group) => group.children),
    [filteredNavGroups]
  );
  const leftDockPinStorageKey = `crmLeftDockPins:${user?.username || 'anon'}`;
  const flatDockPathsKey = useMemo(
    () => flatDockNavItems.map((item) => item.to).join('\u0001'),
    [flatDockNavItems]
  );
  const hasCachedLeftDockPins = useMemo(() => {
    if (typeof window === 'undefined') return false;
    try {
      return !!localStorage.getItem(leftDockPinStorageKey);
    } catch {
      return false;
    }
  }, [leftDockPinStorageKey]);
  const [leftDockPinnedPaths, setLeftDockPinnedPaths] = useState(null);
  const [leftPinSelectedGroupKey, setLeftPinSelectedGroupKey] = useState(null);
  const [leftPinRevealGroupKey, setLeftPinRevealGroupKey] = useState(null);
  const [isBottomDockLauncherOpen, setIsBottomDockLauncherOpen] = useState(false);
  const [dockLimitFlash, setDockLimitFlash] = useState(false);
  const [genieFx, setGenieFx] = useState(null);
  const genieFxIdRef = useRef(0);
  const genieFxTimerRef = useRef(null);
  const mainRef = useRef(null);
  const dockLimitFlashTimerRef = useRef(null);
  const leftPinRevealTimerRef = useRef(null);
  const leftDockPinsSaveGenRef = useRef(0);
  const leftDockPinsSkipReloadUntilRef = useRef(0);
  const leftDockPinsPersistTimerRef = useRef(null);

  useEffect(() => {
    if (!leftPinSelectedGroupKey) {
      setLeftPinRevealGroupKey(null);
      return undefined;
    }
    setLeftPinRevealGroupKey(leftPinSelectedGroupKey);
    if (leftPinRevealTimerRef.current) clearTimeout(leftPinRevealTimerRef.current);
    leftPinRevealTimerRef.current = setTimeout(() => {
      setLeftPinRevealGroupKey(null);
    }, 520);
    return () => {
      if (leftPinRevealTimerRef.current) clearTimeout(leftPinRevealTimerRef.current);
    };
  }, [leftPinSelectedGroupKey]);

  useEffect(() => {
    if (!isBottomNavDock) setIsBottomDockLauncherOpen(false);
  }, [isBottomNavDock]);

  const prefersReducedMotion = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  }, []);

  const bottomDockNavRef = useRef(null);
  useDockMagnify(bottomDockNavRef, { enabled: isBottomNavDock, maxScale: 1.28, influence: 90 });

  const triggerBottomDockGenie = useCallback((event, item) => {
    if (!isBottomNavDock || prefersReducedMotion()) return;
    const trigger = event?.currentTarget;
    if (!trigger || typeof trigger.getBoundingClientRect !== 'function') return;
    const iconEl = trigger.querySelector('.crm-nav-icon') || trigger;
    const rect = iconEl.getBoundingClientRect();
    const originX = rect.left + rect.width / 2;
    const originY = rect.top + rect.height / 2;
    const id = ++genieFxIdRef.current;
    setGenieFx({ id, x: originX, y: originY, icon: item?.icon || '✨' });

    const mainEl = mainRef.current;
    if (mainEl) {
      const mainRect = mainEl.getBoundingClientRect();
      if (mainRect.width > 0) {
        const ox = ((originX - mainRect.left) / mainRect.width) * 100;
        mainEl.style.setProperty('--genie-ox', `${Math.max(0, Math.min(100, ox))}%`);
      }
      mainEl.classList.remove('is-genie-arriving');
      // Force reflow so the animation restarts on rapid re-clicks.
      void mainEl.offsetWidth;
      mainEl.classList.add('is-genie-arriving');
    }

    if (genieFxTimerRef.current) clearTimeout(genieFxTimerRef.current);
    genieFxTimerRef.current = setTimeout(() => {
      setGenieFx(null);
      if (mainRef.current) mainRef.current.classList.remove('is-genie-arriving');
    }, 1050);
  }, [isBottomNavDock, prefersReducedMotion]);

  useEffect(() => () => {
    if (genieFxTimerRef.current) clearTimeout(genieFxTimerRef.current);
  }, []);

  const persistLeftDockPinnedPaths = useCallback(async (pathsSet) => {
    const saveGen = ++leftDockPinsSaveGenRef.current;
    const allowedPaths = flatDockNavItems.map((item) => item.to);
    const pinsPayload = buildLeftDockPinsPayload(pathsSet, flatDockNavItems);
    writeLeftDockPinnedPaths(leftDockPinStorageKey, pathsSet);
    if (!user?.id) return true;

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/left-dock-pins`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pins: pinsPayload })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.warn('[nav-dock-pins] No se pudo guardar:', data?.error || res.status);
        return false;
      }
      if (saveGen !== leftDockPinsSaveGenRef.current) return true;
      leftDockPinsSkipReloadUntilRef.current = Date.now() + 4000;
      const next = data.configured === false
        ? buildLeftDockPinnedSet(data.paths, allowedPaths)
        : Array.isArray(data.pins)
          ? buildLeftDockPinnedSetFromPins(data.pins, allowedPaths)
          : buildLeftDockPinnedSet(data.paths, allowedPaths);
      setLeftDockPinnedPaths(next);
      writeLeftDockPinnedPaths(leftDockPinStorageKey, next);
      return true;
    } catch (err) {
      console.warn('[nav-dock-pins] Error de red al guardar:', err?.message || err);
      return false;
    }
  }, [leftDockPinStorageKey, user?.id, flatDockNavItems]);

  const queuePersistLeftDockPinnedPaths = useCallback((pathsSet) => {
    if (leftDockPinsPersistTimerRef.current) {
      clearTimeout(leftDockPinsPersistTimerRef.current);
    }
    leftDockPinsPersistTimerRef.current = setTimeout(() => {
      leftDockPinsPersistTimerRef.current = null;
      persistLeftDockPinnedPaths(pathsSet);
    }, 280);
  }, [persistLeftDockPinnedPaths]);

  useEffect(() => () => {
    if (leftDockPinsPersistTimerRef.current) {
      clearTimeout(leftDockPinsPersistTimerRef.current);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const allowedPaths = flatDockNavItems.map((item) => item.to);

    async function loadLeftDockPins() {
      if (!allowedPaths.length) {
        if (!cancelled) setLeftDockPinnedPaths(new Set());
        return;
      }

      if (Date.now() < leftDockPinsSkipReloadUntilRef.current) {
        if (!cancelled) {
          setLeftDockPinnedPaths((prev) => {
            if (!prev) return prev;
            const allowed = new Set(allowedPaths);
            const pruned = new Set([...prev].filter((p) => allowed.has(p)));
            return pruned.size > 0 ? pruned : new Set(allowedPaths);
          });
        }
        return;
      }

      if (user?.id) {
        try {
          const res = await fetch(`${API_BASE_URL}/api/auth/left-dock-pins`, {
            credentials: 'include'
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && !cancelled) {
            const next = data.configured === false
              ? buildLeftDockPinnedSet(data.paths, allowedPaths)
              : Array.isArray(data.pins)
                ? buildLeftDockPinnedSetFromPins(data.pins, allowedPaths)
                : buildLeftDockPinnedSet(data.paths, allowedPaths);
            setLeftDockPinnedPaths(next);
            writeLeftDockPinnedPaths(leftDockPinStorageKey, next);
            return;
          }
        } catch {
          // fall through to local cache
        }
      }

      if (!cancelled) {
        setLeftDockPinnedPaths(readLeftDockPinnedPaths(leftDockPinStorageKey, allowedPaths));
      }
    }

    loadLeftDockPins();
    return () => {
      cancelled = true;
    };
  }, [leftDockPinStorageKey, flatDockPathsKey, user?.id]);

  const trimBottomDockPins = useCallback((pathsSet) => {
    if (!isBottomNavDock || !pathsSet || pathsSet.size <= MAX_BOTTOM_DOCK_PINS) {
      return pathsSet;
    }
    const trimmed = new Set();
    flatDockNavItems.forEach((item) => {
      if (pathsSet.has(item.to) && trimmed.size < MAX_BOTTOM_DOCK_PINS) {
        trimmed.add(item.to);
      }
    });
    return trimmed;
  }, [isBottomNavDock, flatDockNavItems]);

  useEffect(() => {
    if (!isBottomNavDock || leftDockPinnedPaths === null) return;
    const trimmed = trimBottomDockPins(leftDockPinnedPaths);
    if (trimmed.size === leftDockPinnedPaths.size) return;
    setLeftDockPinnedPaths(trimmed);
    writeLeftDockPinnedPaths(leftDockPinStorageKey, trimmed);
    queuePersistLeftDockPinnedPaths(trimmed);
  }, [
    isBottomNavDock,
    leftDockPinnedPaths,
    leftDockPinStorageKey,
    trimBottomDockPins,
    queuePersistLeftDockPinnedPaths
  ]);

  const toggleLeftDockPin = useCallback((path) => {
    setLeftDockPinnedPaths((prev) => {
      if (!prev) return prev;
      const next = new Set(prev);
      if (next.has(path)) {
        if (next.size <= 1) return prev;
        next.delete(path);
      } else if (isBottomNavDock && next.size >= MAX_BOTTOM_DOCK_PINS) {
        fireBottomDockLimitAlert(MAX_BOTTOM_DOCK_PINS);
        setDockLimitFlash(true);
        if (dockLimitFlashTimerRef.current) clearTimeout(dockLimitFlashTimerRef.current);
        dockLimitFlashTimerRef.current = setTimeout(() => setDockLimitFlash(false), 2800);
        return prev;
      } else {
        next.add(path);
      }
      const normalized = trimBottomDockPins(next);
      writeLeftDockPinnedPaths(leftDockPinStorageKey, normalized);
      queuePersistLeftDockPinnedPaths(normalized);
      return normalized;
    });
  }, [leftDockPinStorageKey, queuePersistLeftDockPinnedPaths, isBottomNavDock, trimBottomDockPins]);

  const pinLeftDockPath = useCallback((path) => {
    if (!path) return false;
    let added = false;
    setLeftDockPinnedPaths((prev) => {
      if (!prev || prev.has(path)) return prev;
      if (isBottomNavDock && prev.size >= MAX_BOTTOM_DOCK_PINS) {
        fireBottomDockLimitAlert(MAX_BOTTOM_DOCK_PINS);
        setDockLimitFlash(true);
        if (dockLimitFlashTimerRef.current) clearTimeout(dockLimitFlashTimerRef.current);
        dockLimitFlashTimerRef.current = setTimeout(() => setDockLimitFlash(false), 2800);
        return prev;
      }
      added = true;
      const next = trimBottomDockPins(new Set([...prev, path]));
      writeLeftDockPinnedPaths(leftDockPinStorageKey, next);
      queuePersistLeftDockPinnedPaths(next);
      return next;
    });
    return added;
  }, [leftDockPinStorageKey, queuePersistLeftDockPinnedPaths, isBottomNavDock, trimBottomDockPins]);

  const navDockDragPin = useCrmNavDockDragPin({
    enabled: isBottomNavDock,
    onPinPath: (path) => {
      if (pinLeftDockPath(path)) {
        setIsBottomDockLauncherOpen(false);
      }
    }
  });

  const effectiveLeftDockPins = useMemo(() => {
    const allowedPaths = flatDockNavItems.map((item) => item.to);
    if (!allowedPaths.length) return new Set();
    let pins = null;
    if (leftDockPinnedPaths !== null) {
      pins = leftDockPinnedPaths;
    } else if (hasCachedLeftDockPins) {
      pins = readLeftDockPinnedPaths(leftDockPinStorageKey, allowedPaths);
    } else {
      return null;
    }
    return trimBottomDockPins(pins);
  }, [leftDockPinnedPaths, flatDockNavItems, leftDockPinStorageKey, hasCachedLeftDockPins, trimBottomDockPins]);

  const leftDockCollapsedItems = useMemo(() => {
    if (!effectiveLeftDockPins) return [];
    const activePath = String(location.pathname || '');
    return flatDockNavItems.filter(
      (item) => effectiveLeftDockPins.has(item.to) || item.to === activePath
    );
  }, [flatDockNavItems, effectiveLeftDockPins, location.pathname]);

  const leftDockPinnedCount = useMemo(() => {
    if (!effectiveLeftDockPins) return 0;
    return flatDockNavItems.filter((item) => effectiveLeftDockPins.has(item.to)).length;
  }, [flatDockNavItems, effectiveLeftDockPins]);

  const allDockableNavItems = useMemo(
    () => visibleNavGroups.flatMap((group) => group.children),
    [visibleNavGroups]
  );
  const allDockableNavCount = allDockableNavItems.length;
  const allDockPinnedCount = useMemo(() => {
    if (!effectiveLeftDockPins) return 0;
    return allDockableNavItems.filter((item) => effectiveLeftDockPins.has(item.to)).length;
  }, [allDockableNavItems, effectiveLeftDockPins]);

  const dockFlatNavItems = isFloatingNavDock ? leftDockCollapsedItems : flatDockNavItems;

  const activeNavContext = useMemo(() => {
    const path = String(location.pathname || '');
    for (const group of visibleNavGroups) {
      const item = group.children.find((it) => it.to === path);
      if (item) return { group, item };
    }
    return null;
  }, [location.pathname, visibleNavGroups]);
  const hasSearchQuery = String(navSearch || '').trim().length > 0;
  const noSearchResults = hasSearchQuery && totalFilteredNavItems === 0;

  const [expandedNavGroupKey, setExpandedNavGroupKey] = useState('');

  const resolveCollapsedNavTarget = (group) => {
    const children = group?.children || [];
    if (!children.length) return null;
    const activeChild = children.find((it) => location.pathname === it.to);
    if (activeChild) return activeChild.to;
    const unreadChild = children.find((it) => Number(it.unread || 0) > 0);
    if (unreadChild) return unreadChild.to;
    return children[0].to;
  };

  const toggleNavGroup = (groupKey) => {
    if (isDesktopSidebar && sidebarCollapsed) {
      const group = filteredNavGroups.find((g) => g.key === groupKey);
      const target = resolveCollapsedNavTarget(group);
      if (target) navigate(target);
      return;
    }
    setExpandedNavGroupKey((prev) => (prev === groupKey ? '' : groupKey));
  };

  useEffect(() => {
    if (sidebarCollapsed && navSearch) {
      setNavSearch('');
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (sidebarCollapsed) {
      setLeftPinSelectedGroupKey(null);
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!isLeftPremiumExpanded) return;
    const currentPath = String(location.pathname || '');
    const containing = visibleNavGroups.find((g) => g.children.some((it) => it.to === currentPath));
    if (!containing) return;
    setLeftPinSelectedGroupKey((prev) => prev || containing.key);
  }, [isLeftPremiumExpanded, location.pathname, visibleNavGroups]);

  useEffect(() => {
    if (!isLeftPremiumExpanded || !hasSearchQuery) return;
    if (leftPinSelectedGroupKey) return;
    const q = String(navSearch || '').trim().toLowerCase();
    const first = filteredNavGroups.find((group) => {
      if (String(group.label || '').toLowerCase().includes(q)) return group.children.length > 0;
      return group.children.some((it) => String(it.label || '').toLowerCase().includes(q));
    });
    if (first?.key) setLeftPinSelectedGroupKey(first.key);
  }, [isLeftPremiumExpanded, hasSearchQuery, filteredNavGroups, leftPinSelectedGroupKey, navSearch]);

  const getLeftPinVisibleChildrenForGroup = useCallback(
    (group) => {
      if (!group) return [];
      const q = String(navSearch || '').trim().toLowerCase();
      if (!q) return group.children;
      const groupMatch = String(group.label || '').toLowerCase().includes(q);
      if (groupMatch) return group.children;
      return group.children.filter((it) => String(it.label || '').toLowerCase().includes(q));
    },
    [navSearch]
  );

  const leftPinSelectedGroup = useMemo(
    () => filteredNavGroups.find((g) => g.key === leftPinSelectedGroupKey) || null,
    [filteredNavGroups, leftPinSelectedGroupKey]
  );

  const leftPinVisibleChildren = useMemo(
    () => getLeftPinVisibleChildrenForGroup(leftPinSelectedGroup),
    [getLeftPinVisibleChildrenForGroup, leftPinSelectedGroup]
  );

  useEffect(() => {
    const currentPath = String(location.pathname || '');
    const containing = visibleNavGroups.find((g) => g.children.some((it) => it.to === currentPath));
    if (!containing) return;
    setExpandedNavGroupKey((prev) => (prev || containing.key));
  }, [location.pathname, visibleNavGroups]);

  useEffect(() => {
    if (!hasSearchQuery) return;
    if (!expandedNavGroupKey && filteredNavGroups[0]?.key) setExpandedNavGroupKey(filteredNavGroups[0].key);
  }, [hasSearchQuery, filteredNavGroups]);

  useEffect(() => {
    const current = String(navSearch || '').trim().toLowerCase();
    const username = String(user?.username || '').trim().toLowerCase();
    if (current && username && current === username) {
      setNavSearch('');
    }
  }, [navSearch, user?.username]);

  const fetchProfile = async () => {
    setProfileLoading(true);
    setProfileError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/profile`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        reportUnauthorized(await readUnauthorizedMessage(res));
        return;
      }
      if (!res.ok) throw new Error(data.error || 'No se pudo cargar el perfil.');
      setProfileData({
        username: data.username || data.user || user?.username || 'Usuario',
        correo: data.correo || data.email || data.mail || '',
        telefono: data.telefono || data.phone || data.celular || data.telefono_movil || '',
        medico_id: data.medico_id ?? null,
        medico_nombre: data.medico_nombre || '',
        medico_especialidad: data.medico_especialidad || '',
        administrativo_id: data.administrativo_id ?? null,
        administrativo_nombre: data.administrativo_nombre || '',
        callcenter_id: data.callcenter_id ?? null,
        callcenter_nombre: data.callcenter_nombre || ''
      });
      setProfilePhoto(data.profile_photo || '');
      setProfilePhotoOriginal(data.profile_photo_original || data.profile_photo || '');
      setSavedPhotoPreset(data.profile_photo_preset || 'natural');
    } catch (err) {
      setProfileError(err.message || 'No se pudo cargar el perfil.');
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const bootstrapProfilePhoto = async () => {
      if (!user) return;
      const storageKey = navDockEdgeStorageKey(user.username);
      const cachedEdge = readNavDockEdgePref(user.username);
      if (!cancelled) setNavDockEdgePref(cachedEdge);

      if (user.id) {
        try {
          const res = await fetch(`${API_BASE_URL}/api/auth/profile`, { credentials: 'include' });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || cancelled) return;
          if (data.profile_photo) setProfilePhoto(data.profile_photo);
          if (data.profile_photo_original) setProfilePhotoOriginal(data.profile_photo_original);
          if (data.profile_photo_preset) setSavedPhotoPreset(data.profile_photo_preset);
          setProfileData((prev) => ({
            ...prev,
            medico_id: data.medico_id ?? prev.medico_id,
            medico_nombre: data.medico_nombre || prev.medico_nombre,
            medico_especialidad: data.medico_especialidad || prev.medico_especialidad,
            administrativo_id: data.administrativo_id ?? prev.administrativo_id,
            administrativo_nombre: data.administrativo_nombre || prev.administrativo_nombre
            ,
            callcenter_id: data.callcenter_id ?? prev.callcenter_id,
            callcenter_nombre: data.callcenter_nombre || prev.callcenter_nombre
          }));
          if (data.nav_dock_edge === 'left' || data.nav_dock_edge === 'bottom') {
            if (!cancelled) setNavDockEdgePref(data.nav_dock_edge);
            try { localStorage.setItem(storageKey, data.nav_dock_edge); } catch (_) { }
          }
          if (typeof data.nav_dock_left_expanded_fixed === 'boolean') {
            if (!cancelled) {
              setIsLeftDockExpandedFixed(data.nav_dock_left_expanded_fixed);
              if (data.nav_dock_left_expanded_fixed) setIsSidebarCollapsed(false);
            }
            writeNavDockLeftExpandedFixedPref(user.username, data.nav_dock_left_expanded_fixed);
          }
        } catch (_) {
          // cached preference already applied
        }
      }
    };
    bootstrapProfilePhoto();
    return () => { cancelled = true; };
  }, [user]);

  const handleUserClick = async () => {
    setIsProfileDrawerOpen(true);
    setIsPasswordSectionOpen(false);
    setIsProfileExtrasOpen(false);
    await fetchProfile();
  };

  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      const optimizedDataUrl = await optimizeProfileImage(dataUrl);
      const saved = await persistProfilePhoto({
        photoDataUrl: optimizedDataUrl,
        originalPhotoDataUrl: optimizedDataUrl,
        preset: 'natural'
      });
      setProfilePhoto(saved.profilePhoto);
      setProfilePhotoOriginal(saved.profilePhotoOriginal);
      setSavedPhotoPreset(saved.preset);
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'success',
        title: 'Foto actualizada',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
    } catch (_) { }
  };

  const startCamera = async () => {
    setCameraError('');
    setIsCameraReady(false);
    setIsCameraOpen(true);
  };

  const stopCamera = () => {
    const stream = cameraStreamRef.current || videoRef.current?.srcObject;
    if (stream && typeof stream.getTracks === 'function') {
      stream.getTracks().forEach((t) => t.stop());
    }
    cameraStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setIsCameraReady(false);
    setIsCameraOpen(false);
  };

  const captureFromCamera = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    if (!isCameraReady) {
      setCameraError('Esperá un segundo a que la cámara termine de cargar.');
      return;
    }
    const w = video.videoWidth || video.clientWidth || 640;
    const h = video.videoHeight || video.clientHeight || 480;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    try {
      const optimizedDataUrl = await optimizeProfileImage(dataUrl);
      const saved = await persistProfilePhoto({
        photoDataUrl: optimizedDataUrl,
        originalPhotoDataUrl: optimizedDataUrl,
        preset: 'natural'
      });
      setProfilePhoto(saved.profilePhoto);
      setProfilePhotoOriginal(saved.profilePhotoOriginal);
      setSavedPhotoPreset(saved.preset);
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'success',
        title: 'Foto de perfil actualizada',
        showConfirmButton: false,
        timer: 2300,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
      stopCamera();
    } catch (err) {
      setCameraError(err.message || 'No se pudo guardar la foto.');
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'error',
        title: err.message || 'No se pudo guardar la foto.',
        showConfirmButton: false,
        timer: 2800,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
    }
  };

  const getCanvasFilterByPreset = (preset, intensity = 100) => {
    const t = Math.max(0, Math.min(100, Number(intensity) || 100)) / 100;
    switch (preset) {
      case 'cool':
        return `contrast(${1 + 0.12 * t}) saturate(${1 + 0.22 * t}) hue-rotate(${10 * t}deg)`;
      case 'bw':
        return `grayscale(${t}) contrast(${1 + 0.1 * t})`;
      case 'warm':
        return `contrast(${1 + 0.06 * t}) saturate(${1 + 0.2 * t}) sepia(${0.2 * t}) hue-rotate(${-10 * t}deg)`;
      default:
        return 'none';
    }
  };

  const persistProfilePhoto = async ({ photoDataUrl, originalPhotoDataUrl, preset }) => {
    const res = await fetch(`${API_BASE_URL}/api/auth/profile-photo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ photoDataUrl, originalPhotoDataUrl, preset })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'No se pudo guardar la foto.');
    return {
      profilePhoto: data.profile_photo || photoDataUrl,
      profilePhotoOriginal: data.profile_photo_original || originalPhotoDataUrl || photoDataUrl,
      preset: data.profile_photo_preset || preset || 'natural'
    };
  };

  const applyPhotoPreset = async (preset) => {
    const sourcePhoto = profilePhotoOriginal || photoStudioBasePhoto || profilePhoto;
    if (!sourcePhoto || isApplyingPhotoStyle) return;
    setPhotoStylePreset(preset);
    setIsApplyingPhotoStyle(true);
    try {
      if (preset === 'natural') {
        const savedNatural = await persistProfilePhoto({
          photoDataUrl: sourcePhoto,
          originalPhotoDataUrl: sourcePhoto,
          preset: 'natural'
        });
        setProfilePhoto(savedNatural.profilePhoto);
        setProfilePhotoOriginal(savedNatural.profilePhotoOriginal);
        setSavedPhotoPreset(savedNatural.preset);
        setIsApplyingPhotoStyle(false);
        return;
      }
      const img = await imageFromDataUrl(sourcePhoto);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('No se pudo procesar la imagen.');
      ctx.filter = getCanvasFilterByPreset(preset, 100);
      const safeZoom = Math.max(1, Number(photoZoom) || 1);
      const previewRect = photoPreviewRef.current?.getBoundingClientRect();
      const refW = previewRect?.width || canvas.width;
      const refH = previewRect?.height || canvas.height;
      const panScaleX = canvas.width / refW;
      const panScaleY = canvas.height / refH;
      const panX = (photoPan?.x || 0) * panScaleX;
      const panY = (photoPan?.y || 0) * panScaleY;

      const drawW = canvas.width * safeZoom;
      const drawH = canvas.height * safeZoom;
      const drawX = (canvas.width - drawW) / 2 + panX;
      const drawY = (canvas.height - drawH) / 2 + panY;

      ctx.drawImage(img, drawX, drawY, drawW, drawH);
      const styledDataUrl = canvas.toDataURL('image/jpeg', 0.92);
      const saved = await persistProfilePhoto({
        photoDataUrl: styledDataUrl,
        originalPhotoDataUrl: sourcePhoto,
        preset
      });
      setProfilePhoto(saved.profilePhoto);
      setProfilePhotoOriginal(saved.profilePhotoOriginal);
      setSavedPhotoPreset(saved.preset);
      setPhotoFlashTick((v) => v + 1);
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'success',
        title: `Estilo ${preset.toUpperCase()} aplicado`,
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
    } catch (err) {
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'error',
        title: err.message || 'No se pudo aplicar el estilo.',
        showConfirmButton: false,
        timer: 2400,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
    } finally {
      setIsApplyingPhotoStyle(false);
    }
  };

  const restoreOriginalPhoto = async () => {
    if (!profilePhotoOriginal || isApplyingPhotoStyle) return;
    await applyPhotoPreset('natural');
  };

  const handleSaveCurrentPhoto = async () => {
    if (isApplyingPhotoStyle) return;
    await applyPhotoPreset(photoStylePreset || savedPhotoPreset || 'natural');
    Swal.fire({
      toast: true,
      position: 'bottom-end',
      icon: 'success',
      title: 'Cambios de foto guardados',
      showConfirmButton: false,
      timer: 1800,
      timerProgressBar: true,
      customClass: { popup: 'swal-toast-crm' }
    });
  };

  const handlePhotoPreviewMouseDown = (e) => {
    if (photoZoom <= 1) return;
    setIsPhotoDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX: photoPan.x,
      panY: photoPan.y
    };
  };

  const handlePhotoPreviewMouseMove = (e) => {
    if (!isPhotoDragging || photoZoom <= 1) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setPhotoPan({
      x: dragStartRef.current.panX + dx,
      y: dragStartRef.current.panY + dy
    });
  };

  const stopPhotoDrag = () => {
    setIsPhotoDragging(false);
  };

  const resetPasswordFormState = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordChangeInlineFeedback({ type: '', text: '' });
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleCloseProfileDrawer = () => {
    resetPasswordFormState();
    setIsPasswordSectionOpen(false);
    setIsProfileExtrasOpen(false);
    setIsProfileDrawerOpen(false);
  };

  const handleDrawerLogout = async () => {
    resetPasswordFormState();
    setIsPasswordSectionOpen(false);
    setIsProfileExtrasOpen(false);
    await logout();
    setIsProfileDrawerOpen(false);
    navigate('/login');
    Swal.fire({
      toast: true,
      position: 'bottom-end',
      icon: 'success',
      title: 'Sesión cerrada',
      showConfirmButton: false,
      timer: 2200,
      timerProgressBar: true,
      customClass: { popup: 'swal-toast-crm' }
    });
  };

  const copyProfileValue = async (value, label) => {
    const safeValue = String(value || '').trim();
    if (!safeValue) {
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'warning',
        title: `No hay ${label.toLowerCase()} para copiar`,
        showConfirmButton: false,
        timer: 2200,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
      return;
    }
    try {
      await navigator.clipboard.writeText(safeValue);
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'success',
        title: `${label} copiado`,
        showConfirmButton: false,
        timer: 1900,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
    } catch (_) {
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'error',
        title: `No se pudo copiar ${label.toLowerCase()}`,
        showConfirmButton: false,
        timer: 2400,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (isReservedForN8nProfile(profileData, user)) {
      const msg = 'Este usuario está reservado para n8n y no puede cambiar contraseña desde el perfil.';
      setPasswordChangeInlineFeedback({ type: 'error', text: msg });
      Swal.fire({ toast: true, position: 'bottom-end', icon: 'warning', title: msg, showConfirmButton: false, timer: 3200, timerProgressBar: true, customClass: { popup: 'swal-toast-crm' } });
      return;
    }
    const hasCurrentPassword = String(currentPassword || '').trim().length > 0;
    const hasNewPassword = String(newPassword || '').length > 0;
    const hasConfirmPassword = String(confirmPassword || '').length > 0;
    const nextPassword = String(newPassword || '');
    const isNewPasswordStrong =
      nextPassword.length >= 8
      && /[A-Z]/.test(nextPassword)
      && /[a-z]/.test(nextPassword)
      && /\d/.test(nextPassword)
      && /[^A-Za-z0-9]/.test(nextPassword);
    const passwordsMatch = hasConfirmPassword && String(newPassword || '') === String(confirmPassword || '');

    if (changingPassword) return;
    if (!hasCurrentPassword) {
      setPasswordChangeInlineFeedback({ type: 'error', text: 'Ingresá tu contraseña actual.' });
      Swal.fire({ toast: true, position: 'bottom-end', icon: 'warning', title: 'Ingresá tu contraseña actual.', showConfirmButton: false, timer: 2600, timerProgressBar: true, customClass: { popup: 'swal-toast-crm' } });
      return;
    }
    if (!hasNewPassword) {
      setPasswordChangeInlineFeedback({ type: 'error', text: 'Ingresá una nueva contraseña.' });
      Swal.fire({ toast: true, position: 'bottom-end', icon: 'warning', title: 'Ingresá una nueva contraseña.', showConfirmButton: false, timer: 2600, timerProgressBar: true, customClass: { popup: 'swal-toast-crm' } });
      return;
    }
    if (!isNewPasswordStrong) {
      const msg = 'La nueva contraseña debe tener mínimo 8 caracteres, mayúscula, minúscula, número y símbolo.';
      setPasswordChangeInlineFeedback({ type: 'error', text: msg });
      Swal.fire({ toast: true, position: 'bottom-end', icon: 'warning', title: msg, showConfirmButton: false, timer: 3400, timerProgressBar: true, customClass: { popup: 'swal-toast-crm' } });
      return;
    }
    if (String(currentPassword || '') === nextPassword) {
      const msg = 'La nueva contraseña debe ser distinta a la actual.';
      setPasswordChangeInlineFeedback({ type: 'error', text: msg });
      Swal.fire({ toast: true, position: 'bottom-end', icon: 'warning', title: msg, showConfirmButton: false, timer: 2800, timerProgressBar: true, customClass: { popup: 'swal-toast-crm' } });
      return;
    }
    if (!hasConfirmPassword) {
      setPasswordChangeInlineFeedback({ type: 'error', text: 'Repetí la nueva contraseña.' });
      Swal.fire({ toast: true, position: 'bottom-end', icon: 'warning', title: 'Repetí la nueva contraseña.', showConfirmButton: false, timer: 2600, timerProgressBar: true, customClass: { popup: 'swal-toast-crm' } });
      return;
    }
    if (!passwordsMatch) {
      setPasswordChangeInlineFeedback({ type: 'error', text: 'Las contraseñas nuevas no coinciden.' });
      Swal.fire({ toast: true, position: 'bottom-end', icon: 'error', title: 'Las contraseñas nuevas no coinciden.', showConfirmButton: false, timer: 2800, timerProgressBar: true, customClass: { popup: 'swal-toast-crm' } });
      return;
    }

    setPasswordChangeInlineFeedback({ type: '', text: '' });
    setChangingPassword(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'No se pudo actualizar la contraseña.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordChangeInlineFeedback({ type: 'success', text: data.message || 'Contraseña actualizada correctamente.' });
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'success',
        title: data.message || 'Contraseña actualizada',
        showConfirmButton: false,
        timer: 2600,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
    } catch (err) {
      setPasswordChangeInlineFeedback({ type: 'error', text: err.message || 'No se pudo actualizar la contraseña.' });
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'error',
        title: err.message || 'No se pudo actualizar la contraseña.',
        showConfirmButton: false,
        timer: 2800,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
    } finally {
      setChangingPassword(false);
    }
  };

  const hasCurrentPassword = String(currentPassword || '').trim().length > 0;
  const hasNewPassword = String(newPassword || '').length > 0;
  const hasConfirmPassword = String(confirmPassword || '').length > 0;
  const isReservedN8nProfile = isReservedForN8nProfile(profileData, user);
  const canEditCurrentPassword = !isReservedN8nProfile && !changingPassword;
  const canEditNewPassword = !isReservedN8nProfile && hasCurrentPassword && !changingPassword;
  const canEditConfirmPassword = canEditNewPassword && hasNewPassword;
  const isNewPasswordStrong =
    String(newPassword || '').length >= 8
    && /[A-Z]/.test(String(newPassword || ''))
    && /[a-z]/.test(String(newPassword || ''))
    && /\d/.test(String(newPassword || ''))
    && /[^A-Za-z0-9]/.test(String(newPassword || ''));
  const passwordsMatch = hasConfirmPassword && String(newPassword || '') === String(confirmPassword || '');
  const canSubmitPassword = !isReservedN8nProfile && hasCurrentPassword && hasNewPassword && hasConfirmPassword && !changingPassword;
  const isPasswordFormValid = canSubmitPassword && isNewPasswordStrong && passwordsMatch && String(currentPassword || '') !== String(newPassword || '');
  const passwordChecks = {
    length: String(newPassword || '').length >= 8,
    upper: /[A-Z]/.test(String(newPassword || '')),
    lower: /[a-z]/.test(String(newPassword || '')),
    number: /\d/.test(String(newPassword || '')),
    symbol: /[^A-Za-z0-9]/.test(String(newPassword || ''))
  };
  const passwordStrengthScore = [
    passwordChecks.length,
    passwordChecks.upper,
    passwordChecks.lower,
    passwordChecks.number,
    passwordChecks.symbol
  ].filter(Boolean).length;
  const passwordStrengthLabel = passwordStrengthScore <= 2
    ? 'Baja'
    : passwordStrengthScore <= 4
      ? 'Media'
      : 'Alta';
  const passwordStrengthHint = passwordStrengthScore <= 2
    ? 'Aumentá complejidad para proteger tu cuenta.'
    : passwordStrengthScore <= 4
      ? 'Vas bien, agregá un símbolo extra para reforzarla.'
      : 'Excelente nivel de seguridad.';
  const profileLinkedCatalogs = useMemo(
    () => resolveProfileLinkedCatalogs(profileData, user),
    [profileData, user]
  );
  const layoutPersonDisplayName = useMemo(
    () => resolveLayoutPersonDisplayName(profileData, user),
    [profileData, user]
  );
  const layoutPersonDisplayInitial = useMemo(
    () => resolveLayoutPersonDisplayInitial(profileData, user),
    [profileData, user]
  );
  const layoutPersonRoleSubtitle = useMemo(
    () => resolveLayoutPersonRoleSubtitle(profileLinkedCatalogs),
    [profileLinkedCatalogs]
  );
  const hasEmail = Boolean(String(profileData.correo || '').trim());
  const hasPhone = Boolean(String(profileData.telefono || '').trim());
  const hasPhoto = Boolean(String(profilePhoto || '').trim());
  const hasCatalogLink = profileLinkedCatalogs.length > 0;
  const profileCompletionScore = [hasEmail, hasPhone, hasPhoto, hasCatalogLink].filter(Boolean).length;
  const profileCompletionPercent = Math.round((profileCompletionScore / 4) * 100);
  let passwordHelperMessage = 'Primero ingresá tu contraseña actual para desbloquear los siguientes campos.';
  if (isReservedN8nProfile) {
    passwordHelperMessage = 'Este usuario está reservado para n8n. El cambio de contraseña está bloqueado.';
  } else {
    if (hasCurrentPassword && !hasNewPassword) passwordHelperMessage = 'Ahora ingresá una nueva contraseña.';
    if (hasCurrentPassword && hasNewPassword && !isNewPasswordStrong) passwordHelperMessage = 'La nueva contraseña debe tener 8+ caracteres, mayúscula, minúscula, número y símbolo.';
    if (hasCurrentPassword && hasNewPassword && String(currentPassword || '') === String(newPassword || '')) passwordHelperMessage = 'La nueva contraseña debe ser distinta a la actual.';
    if (hasCurrentPassword && hasNewPassword && isNewPasswordStrong && !hasConfirmPassword) passwordHelperMessage = 'Repetí la nueva contraseña para confirmar.';
    if (hasCurrentPassword && hasNewPassword && hasConfirmPassword && !passwordsMatch) passwordHelperMessage = 'Las contraseñas nuevas no coinciden.';
    if (isPasswordFormValid) passwordHelperMessage = 'Listo para guardar. Las contraseñas coinciden.';
  }
  const hasLivePasswordError =
    (hasConfirmPassword && !passwordsMatch)
    || (hasNewPassword && !isNewPasswordStrong)
    || (hasCurrentPassword && hasNewPassword && String(currentPassword || '') === String(newPassword || ''));
  const showInlinePasswordHelper =
    !isPasswordFormValid && !passwordChangeInlineFeedback.text && !hasLivePasswordError;
  const liveFloatingPasswordMessage = hasLivePasswordError
    ? { type: 'error', text: passwordHelperMessage }
    : (isPasswordFormValid ? { type: 'success', text: passwordHelperMessage } : null);

  useEffect(() => {
    const nextMessage = passwordChangeInlineFeedback.text
      ? passwordChangeInlineFeedback
      : liveFloatingPasswordMessage;

    if (!nextMessage || !nextMessage.text) {
      setFloatingPasswordNote(null);
      return undefined;
    }

    setFloatingPasswordNote(nextMessage);
    const timer = setTimeout(() => {
      setFloatingPasswordNote(null);
    }, 2600);
    return () => clearTimeout(timer);
  }, [
    passwordChangeInlineFeedback.text,
    passwordChangeInlineFeedback.type,
    liveFloatingPasswordMessage?.text,
    liveFloatingPasswordMessage?.type
  ]);

  // Fullscreen / Kiosk handlers
  useEffect(() => {
    const onFullScreenChange = () => {
      const active = !!document.fullscreenElement;
      setIsKiosk(active);
      if (!active) document.body.classList.remove('kiosk-mode');
    };
    document.addEventListener('fullscreenchange', onFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullScreenChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await exitKioskMode();
      if (!cancelled) setIsKiosk(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && isProfileDrawerOpen) {
        handleCloseProfileDrawer();
        return;
      }
      if (e.key === 'Escape' && isMobileNavOpen) {
        setIsMobileNavOpen(false);
        return;
      }
      if (e.key === 'Escape' && document.fullscreenElement) {
        document.exitFullscreen().catch(() => { });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobileNavOpen, isProfileDrawerOpen]);

  useEffect(() => {
    setIsMobileNavOpen(false);
    if (isDesktopSidebar && !isLeftDockExpandedFixed) {
      setIsSidebarCollapsed(true);
    }
  }, [location.pathname, isDesktopSidebar, isLeftDockExpandedFixed]);

  useEffect(() => {
    const fixed = readNavDockLeftExpandedFixedPref(user?.username);
    setIsLeftDockExpandedFixed(fixed);
    if (fixed) {
      setIsSidebarCollapsed(false);
    }
  }, [user?.username]);

  useEffect(() => () => stopCamera(), []);

  useEffect(() => {
    if (!isCameraOpen) return undefined;
    let cancelled = false;
    setCameraError('');
    setIsCameraReady(false);

    const bootCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
          audio: false
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        cameraStreamRef.current = stream;
        if (!videoRef.current) return;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();

        // Esperar a que haya frame real disponible.
        const readyByDimensions =
          (videoRef.current.videoWidth || 0) > 0 && (videoRef.current.videoHeight || 0) > 0;
        if (readyByDimensions) {
          setIsCameraReady(true);
          return;
        }
        const timer = setTimeout(() => {
          if (!cancelled) setIsCameraReady(true);
        }, 300);
        return () => clearTimeout(timer);
      } catch (_) {
        if (!cancelled) {
          setCameraError('No pudimos acceder a la cámara. Verificá permisos del navegador.');
        }
      }
      return undefined;
    };

    bootCamera();
    return () => {
      cancelled = true;
    };
  }, [isCameraOpen]);

  useEffect(() => {
    const onMouseUp = () => setIsPhotoDragging(false);
    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, []);

  useEffect(() => {
    if (!isMobileNavOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isMobileNavOpen]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 769px)');
    const syncViewport = () => setIsDesktopSidebar(mq.matches);
    syncViewport();
    mq.addEventListener('change', syncViewport);
    return () => mq.removeEventListener('change', syncViewport);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('crmSidebarCollapsed', isSidebarCollapsed ? '1' : '0');
    } catch (e) {
      // ignore storage errors
    }
  }, [isSidebarCollapsed]);

  const toggleKiosk = async () => {
    await toggleKioskMode();
    setIsKiosk(isKioskModeActive());
  };

  const ProfileDrawerShell = isBottomNavDock ? 'div' : 'aside';
  const profileDrawerClassName = [
    'crm-profile-drawer',
    isProfileDrawerOpen ? 'is-open' : '',
    isBottomNavDock ? 'crm-profile-drawer--centered' : ''
  ].filter(Boolean).join(' ');

  const renderKioskModeButton = (extraClassName = '') => (
    <button
      type="button"
      onClick={toggleKiosk}
      className={`crm-kiosk-btn ${extraClassName} ${isKiosk ? 'is-active' : ''}`.trim()}
      aria-label={isKiosk ? 'Salir modo Kiosko' : 'Modo Kiosko'}
      title={isKiosk ? 'Salir modo Kiosko (Esc para salir)' : 'Modo Kiosko (Esc para salir)'}
    >
      {isKiosk ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      ) : (
        <IconMonitor />
      )}
    </button>
  );

  return (
    <div
      className={`crm-app ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${!isDesktopSidebar ? 'crm-app--mobile' : ''} ${isFloatingNavDock ? 'crm-app--nav-docked' : ''} ${isFloatingNavDock && navDock.dockEdge === 'left' ? 'crm-app--nav-dock-left' : ''} ${isFloatingNavDock && navDock.dockEdge === 'bottom' ? 'crm-app--nav-dock-bottom' : ''} ${isBottomDockLauncherOpen ? 'crm-app--bottom-launcher-open' : ''} ${isLeftPremiumExpanded ? 'crm-app--sidebar-left-premium' : ''}`}
    >
      <MaintenanceBarrier />
      {isFloatingNavDock && navDock.isDragging ? (
        <div className="crm-nav-dock-edge-hints" aria-hidden="true">
          {CRM_NAV_DOCK_EDGES.map((edge) => (
            <span
              key={edge}
              className={`crm-nav-dock-edge-hint crm-nav-dock-edge-hint--${edge}${navDock.previewEdge === edge ? ' is-active' : ''}`}
            />
          ))}
        </div>
      ) : null}
      <aside
        className={`crm-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${!isDesktopSidebar || isLeftPremiumExpanded ? 'crm-nav-skin-celeste' : ''} ${isFloatingNavDock ? `crm-nav-dock crm-nav-dock--${navDock.dockEdge}` : ''} ${isLeftPremiumExpanded ? 'crm-sidebar--left-premium' : ''} ${navDock.isDragging ? 'is-dragging' : ''}`}
      >
        {!isDesktopSidebar ? (
          <header className="crm-mobile-topbar">
            <Link to="/" className="crm-mobile-topbar-brand" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="crm-sb-logo-mark" aria-hidden="true">
                <span className="crm-sb-logo-core">P</span>
              </div>
              <div className="crm-mobile-topbar-brand-copy">
                <span className="crm-mobile-topbar-app">Grupo Bienestar</span>
              </div>
            </Link>
            <div className="crm-mobile-topbar-context" aria-live="polite">
              <span className="crm-mobile-topbar-module">
                {activeNavContext?.item?.icon ? (
                  <span className="crm-mobile-topbar-module-icon" aria-hidden="true">{activeNavContext.item.icon}</span>
                ) : null}
                <span className="crm-mobile-topbar-module-label">
                  {activeNavContext?.item?.label || 'Inicio'}
                </span>
              </span>
              {activeNavContext?.group ? (
                <span className="crm-mobile-topbar-section">{activeNavContext.group.label}</span>
              ) : null}
            </div>
            <button
              type="button"
              className="crm-mobile-menu-btn"
              onClick={() => setIsMobileNavOpen(true)}
              aria-label="Abrir menú de navegación"
              aria-expanded={isMobileNavOpen}
            >
              <IconMenu />
            </button>
          </header>
        ) : null}
        <div className={`crm-sb-header ${!isDesktopSidebar ? 'crm-sb-header--desktop-only' : ''}`}>
          <Link to="/" className="crm-sb-brand" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div className="crm-sb-logo-mark" aria-hidden="true">
              <span className="crm-sb-logo-core">GSA</span>
            </div>
            <div className="crm-sb-brand-copy">
              <div className="crm-sb-name">Grupo Bienestar</div>
              <div className="crm-sb-tag">CRM · Gestión Asociativa</div>
            </div>
          </Link>
          {isSideNavDock ? (
            <div className="crm-nav-dock__toolbar crm-nav-dock__toolbar--below-brand">
              <NavDockDragHandle onStartDrag={navDock.startDrag} />
              {isDesktopSidebar ? (
                <button
                  type="button"
                  onClick={handleToggleSidebarCollapsed}
                  className={`crm-collapse-btn crm-nav-dock-left-expand ${sidebarCollapsed ? 'is-collapsed' : ''}`}
                  aria-expanded={!sidebarCollapsed}
                  title={sidebarCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
                  aria-label={sidebarCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
                >
                  <IconChevronLeft />
                </button>
              ) : null}
            </div>
          ) : null}
          {isBottomNavDock ? (
            <div className="crm-nav-dock__toolbar crm-nav-dock__toolbar--inline crm-nav-dock__toolbar--bottom-controls">
              <NavDockDragHandle onStartDrag={navDock.startDrag} className="crm-nav-dock-bottom-drag" />
              {user ? renderKioskModeButton('crm-nav-dock-bottom-kiosk') : null}
            </div>
          ) : null}
          {user && !isBottomNavDock ? (
            <div className="crm-sb-header-actions">
              {isDesktopSidebar && !isSideNavDock ? (
                <button
                  type="button"
                  onClick={handleToggleSidebarCollapsed}
                  className={`crm-collapse-btn ${sidebarCollapsed ? 'is-collapsed' : ''}`}
                  aria-expanded={!sidebarCollapsed}
                  title={sidebarCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
                  aria-label={sidebarCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
                >
                  <IconChevronLeft />
                </button>
              ) : null}
              {renderKioskModeButton()}
              {isLeftPremiumExpanded ? (
                <div
                  className="crm-nav-dock-left-fix-inline"
                  title="Mantener expandido al navegar"
                >
                  <span className="crm-nav-dock-left-fix-inline__label">Menú fijo</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isLeftDockExpandedFixed}
                    className={`crm-nav-lx-pin-switch crm-nav-dock-left-fix-switch${isLeftDockExpandedFixed ? ' is-on' : ''}`}
                    aria-label={
                      isLeftDockExpandedFixed
                        ? 'Desfijar menú lateral'
                        : 'Fijar menú lateral — mantener expandido al navegar'
                    }
                    onClick={handleToggleLeftDockExpandedFixed}
                  >
                    <span className="crm-nav-lx-pin-switch-track" aria-hidden="true" />
                    <span className="crm-nav-lx-pin-switch-thumb" aria-hidden="true" />
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className={`crm-sb-sec ${!isDesktopSidebar ? 'crm-sb-sec--desktop-only' : ''}`}>
          <div className="crm-sb-nav-block">
            <div className="crm-sb-lbl">Navegación</div>
            {!sidebarCollapsed ? (
              <NavSearchField
                value={navSearch}
                onChange={(e) => setNavSearch(e.target.value)}
                inputName="crm-nav-search-desktop"
              />
            ) : null}
            <div className="crm-nav-mobile-wrap crm-nav-mobile-wrap--legacy">
              <button
                type="button"
                className="crm-nav-mobile-trigger"
                onClick={() => setIsMobileNavOpen(true)}
                aria-label="Abrir navegación"
              >
                <span className="crm-nav-mobile-trigger-text">Navegación rápida</span>
                <span className="crm-nav-mobile-trigger-icon">☰</span>
              </button>
            </div>
            {isFloatingNavDock ? (
              <div
                ref={isBottomNavDock ? bottomDockNavRef : undefined}
                className={`crm-nav-dock-flat-nav${isSideNavDock ? ' crm-nav-dock-flat-nav--vertical' : ''}${isBottomNavDock ? ' crm-dock-magnify' : ''}${navDockDragPin.isDockDropActive ? ' is-drop-target' : ''}${navDockDragPin.draggingPath ? ' is-receiving-drag' : ''}`}
                {...(isBottomNavDock ? navDockDragPin.dockDropZoneProps : {})}
              >
                {isBottomNavDock ? (
                  <>
                    <CrmBottomDockLauncherButton
                      isOpen={isBottomDockLauncherOpen}
                      onClick={() => setIsBottomDockLauncherOpen((v) => !v)}
                    />
                    <NavSearchField
                      value={navSearch}
                      onChange={(e) => {
                        const next = e.target.value;
                        setNavSearch(next);
                        if (String(next || '').trim()) setIsBottomDockLauncherOpen(true);
                      }}
                      onFocus={() => setIsBottomDockLauncherOpen(true)}
                      inputName="crm-nav-search-bottom-dock"
                      className="crm-nav-search-bottom-dock"
                      placeholder="Buscar módulo o área…"
                    />
                  </>
                ) : null}
                {dockFlatNavItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={(event) => triggerBottomDockGenie(event, item)}
                    className={({ isActive }) =>
                      `crm-nav-item crm-nav-item-child crm-nav-dock-flat-item${isActive ? ' active' : ''}`
                    }
                    title={item.label}
                  >
                    <span className="crm-nav-icon" aria-hidden>{item.icon}</span>
                    <span className="crm-nav-item-label">{item.label}</span>
                    {Number(item.unread || 0) > 0 ? (
                      <span className="crm-nav-badge">{Number(item.unread || 0)}</span>
                    ) : null}
                  </NavLink>
                ))}
              </div>
            ) : isLeftPremiumExpanded ? (
              <div className="crm-nav-lx-pin-board">
                {/* <p className="crm-nav-lx-pin-hint">
                  Elegí un área y activá qué accesos van al menú compacto
                </p> */}
                <p className="crm-nav-lx-pin-meta">
                  {leftDockPinnedCount} de {flatDockNavItems.length} en barra colapsada
                </p>
                <div className="crm-nav-lx-pin-groups" role="tablist" aria-label="Áreas del menú">
                  {filteredNavGroups.map((group) => {
                    const q = String(navSearch || '').trim().toLowerCase();
                    const groupMatch = q && String(group.label || '').toLowerCase().includes(q);
                    const matchesSearch = !q
                      || groupMatch
                      || group.children.some((it) => String(it.label || '').toLowerCase().includes(q));
                    if (!matchesSearch) return null;
                    const isSelected = leftPinSelectedGroupKey === group.key;
                    const isGroupActive = group.children.some((it) => location.pathname === it.to);
                    const pinnedInGroup = group.children.filter(
                      (it) => effectiveLeftDockPins?.has(it.to)
                    ).length;
                    const groupUnread = group.children.reduce(
                      (acc, it) => acc + Number(it.unread || 0),
                      0
                    );
                    const visibleChildren = getLeftPinVisibleChildrenForGroup(group);
                    const isRevealing = leftPinRevealGroupKey === group.key;
                    return (
                      <div
                        key={`lx-pin-group-${group.key}`}
                        className={`crm-nav-lx-pin-group-block${isSelected ? ' is-open' : ''}${isGroupActive ? ' has-active-child' : ''}${isRevealing ? ' is-revealing' : ''}`}
                      >
                        <button
                          type="button"
                          role="tab"
                          aria-selected={isSelected}
                          className={`crm-nav-lx-pin-group-btn${isSelected ? ' is-selected' : ''}${isGroupActive ? ' is-active-route' : ''}`}
                          onClick={() =>
                            setLeftPinSelectedGroupKey((prev) => (prev === group.key ? null : group.key))
                          }
                        >
                          <span className="crm-nav-lx-pin-group-btn-icon-wrap">
                            <span className="crm-nav-lx-pin-group-btn-icon" aria-hidden>{group.icon}</span>
                            {groupUnread > 0 ? (
                              <span className="crm-nav-badge crm-nav-lx-pin-group-btn-badge">{groupUnread}</span>
                            ) : null}
                          </span>
                          <span className="crm-nav-lx-pin-group-btn-copy">
                            <span className="crm-nav-lx-pin-group-btn-label">{group.label}</span>
                            <span className="crm-nav-lx-pin-group-btn-sub">
                              {pinnedInGroup}/{group.children.length} en compacto
                            </span>
                          </span>
                          <span className={`crm-nav-lx-pin-group-btn-chevron${isSelected ? ' is-open' : ''}`} aria-hidden>▾</span>
                        </button>
                        <div
                          className={`crm-nav-lx-pin-children-wrap${isSelected ? ' is-open' : ''}${isRevealing ? ' is-revealing' : ''}`}
                          aria-hidden={!isSelected}
                        >
                          {visibleChildren.length > 0 ? (
                            <div
                              className="crm-nav-lx-pin-children"
                              role="tabpanel"
                              aria-label={`Accesos de ${group.label}`}
                            >
                              <ul className="crm-nav-lx-pin-list">
                                {visibleChildren.map((item) => {
                                  const isPinned = effectiveLeftDockPins ? effectiveLeftDockPins.has(item.to) : false;
                                  const isOnlyPinned = effectiveLeftDockPins?.size === 1 && isPinned;
                                  const isCurrent = location.pathname === item.to;
                                  return (
                                    <li
                                      key={`lx-pin-row-${item.to}`}
                                      className={`crm-nav-lx-pin-row${isCurrent ? ' is-current' : ''}`}
                                    >
                                      <NavLink
                                        to={item.to}
                                        className={({ isActive }) =>
                                          `crm-nav-lx-pin-row-main${isActive ? ' active' : ''}`
                                        }
                                        title={item.label}
                                      >
                                        <span className="crm-nav-lx-pin-row-icon-wrap">
                                          <span className="crm-nav-lx-pin-row-icon" aria-hidden>{item.icon}</span>
                                          {Number(item.unread || 0) > 0 ? (
                                            <span className="crm-nav-badge crm-nav-lx-pin-row-badge">
                                              {Number(item.unread || 0)}
                                            </span>
                                          ) : null}
                                        </span>
                                        <span className="crm-nav-lx-pin-row-label">{item.label}</span>
                                      </NavLink>
                                      <div className="crm-nav-lx-pin-switch-wrap">
                                        <button
                                          type="button"
                                          role="switch"
                                          aria-checked={isPinned}
                                          disabled={!effectiveLeftDockPins || (isOnlyPinned && isPinned)}
                                          className={`crm-nav-lx-pin-switch${isPinned ? ' is-on' : ''}`}
                                          aria-label={`${isPinned ? 'Ocultar' : 'Mostrar'} ${item.label} en menú compacto`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleLeftDockPin(item.to);
                                          }}
                                        >
                                          <span className="crm-nav-lx-pin-switch-track" aria-hidden="true" />
                                          <span className="crm-nav-lx-pin-switch-thumb" aria-hidden="true" />
                                        </button>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          ) : (
                            <div className="crm-nav-lx-pin-empty crm-nav-lx-pin-empty--nested">
                              No hay accesos para esta búsqueda
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              filteredNavGroups.map((group) => {
                const isGroupOpen = expandedNavGroupKey === group.key;
                const groupUnread = group.children.reduce((acc, it) => acc + Number(it.unread || 0), 0);
                const isGroupActive = group.children.some((it) => location.pathname === it.to);
                const collapsedNavTarget = sidebarCollapsed ? resolveCollapsedNavTarget(group) : null;
                const collapsedNavLabel = collapsedNavTarget
                  ? group.children.find((it) => it.to === collapsedNavTarget)?.label
                  : '';
                return (
                  <div key={`group-${group.key}`} className={`crm-nav-group ${isGroupOpen ? 'is-open' : ''}`}>
                    <button
                      type="button"
                      className={`crm-nav-group-toggle ${isGroupActive ? 'is-active' : ''}`}
                      onClick={() => toggleNavGroup(group.key)}
                      aria-expanded={isGroupOpen}
                      title={
                        sidebarCollapsed && collapsedNavLabel
                          ? `Ir a ${collapsedNavLabel}`
                          : group.label
                      }
                    >
                      <span className="crm-nav-group-main">
                        <span className="crm-nav-group-icon" aria-hidden>{group.icon}</span>
                        <span className="crm-nav-group-label">{group.label}</span>
                      </span>
                      <span className="crm-nav-group-right">
                        {groupUnread > 0 ? (
                          <span className="crm-nav-badge">{groupUnread}</span>
                        ) : null}
                        <span className={`crm-nav-group-chevron ${isGroupOpen ? 'is-open' : ''}`} aria-hidden>▾</span>
                      </span>
                    </button>
                    <div className={`crm-nav-group-children ${isGroupOpen ? 'is-open' : ''}`}>
                      {group.children.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          className={({ isActive }) =>
                            'crm-nav-item crm-nav-item-child' + (isActive ? ' active' : '')
                          }
                        >
                          <span className="crm-nav-icon">{item.icon}</span>
                          <span className="crm-nav-item-label">{item.label}</span>
                          {Number(item.unread || 0) > 0 ? (
                            <span className="crm-nav-badge">{Number(item.unread || 0)}</span>
                          ) : null}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
            {noSearchResults && (
              <div className="crm-nav-empty">
                <strong>Sin resultados</strong>
                <span>No encontramos módulos para &quot;{navSearch.trim()}&quot;.</span>
              </div>
            )}
          </div>

          <div className="crm-sb-user-block">
            <div className="crm-sb-lbl">Usuario</div>
            <div className="crm-sb-user-card">
              <button
                type="button"
                className="crm-sb-foot crm-sb-foot-btn"
                onClick={handleUserClick}
                aria-label="Abrir perfil"
              >
                <div className="crm-u-ava-wrap">
                  <div className="crm-u-ava">
                    {profilePhoto ? (
                      <img src={profilePhoto} alt="Avatar usuario" className="crm-u-ava-img" />
                    ) : (
                      layoutPersonDisplayInitial
                    )}
                  </div>
                  <span className="crm-u-status" aria-hidden="true" />
                </div>
                <div className="crm-u-meta">
                  <div className="crm-u-name" title={user?.username || undefined}>{layoutPersonDisplayName}</div>
                  <div className="crm-u-role">{layoutPersonRoleSubtitle}</div>
                </div>
                <IconChevronRightSmall />
              </button>
            </div>
          </div>
        </div>

      </aside>
      <div
        className={`crm-mobile-nav-overlay ${isMobileNavOpen ? 'is-open' : ''}`}
        onClick={() => setIsMobileNavOpen(false)}
        aria-hidden={!isMobileNavOpen}
      />
      <aside
        className={`crm-mobile-nav-drawer crm-nav-skin-celeste ${isMobileNavOpen ? 'is-open' : ''}`}
        aria-hidden={!isMobileNavOpen}
        role="dialog"
        aria-modal="true"
        aria-label="Menú de navegación"
      >
        <div className="crm-mobile-nav-drawer-head">
          <Link to="/" className="crm-mobile-nav-drawer-brand" style={{ textDecoration: 'none', color: 'inherit' }} onClick={() => setIsMobileNavOpen(false)}>
            <div className="crm-sb-logo-mark" aria-hidden="true">
              <span className="crm-sb-logo-core">P</span>
            </div>
            <div className="crm-sb-brand-copy">
              <div className="crm-sb-name">Grupo Bienestar</div>
              <div className="crm-sb-tag">CRM · Gestión Asociativa</div>
            </div>
          </Link>
          <button
            type="button"
            className="crm-mobile-nav-drawer-close"
            onClick={() => setIsMobileNavOpen(false)}
            aria-label="Cerrar navegación"
          >
            <IconClose />
          </button>
        </div>
        <div className="crm-mobile-nav-drawer-scroll">
          <div className="crm-mobile-nav-drawer-search">
            <div className="crm-sb-lbl">Navegación</div>
            <NavSearchField
              value={navSearch}
              onChange={(e) => setNavSearch(e.target.value)}
              inputName="crm-nav-search-mobile"
              className="crm-nav-search-mobile-field"
            />
          </div>
          <nav className="crm-mobile-nav-drawer-nav" aria-label="Módulos">
            {filteredNavGroups.map((group) => {
              const isGroupOpen = expandedNavGroupKey === group.key;
              const groupUnread = group.children.reduce((acc, it) => acc + Number(it.unread || 0), 0);
              const isGroupActive = group.children.some((it) => location.pathname === it.to);
              return (
                <div key={`mobile-group-${group.key}`} className={`crm-nav-group ${isGroupOpen ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    className={`crm-nav-group-toggle ${isGroupActive ? 'is-active' : ''}`}
                    onClick={() => toggleNavGroup(group.key)}
                    aria-expanded={isGroupOpen}
                  >
                    <span className="crm-nav-group-main">
                      <span className="crm-nav-group-icon" aria-hidden>{group.icon}</span>
                      <span className="crm-nav-group-label">{group.label}</span>
                    </span>
                    <span className="crm-nav-group-right">
                      {groupUnread > 0 ? (
                        <span className="crm-nav-badge">{groupUnread}</span>
                      ) : null}
                      <span className={`crm-nav-group-chevron ${isGroupOpen ? 'is-open' : ''}`} aria-hidden>▾</span>
                    </span>
                  </button>
                  <div className={`crm-nav-group-children ${isGroupOpen ? 'is-open' : ''}`}>
                    {group.children.map((item) => (
                      <NavLink
                        key={`mobile-nav-${group.key}-${item.to}`}
                        to={item.to}
                        onClick={() => setIsMobileNavOpen(false)}
                        className={({ isActive }) =>
                          `crm-nav-item crm-nav-item-child${isActive ? ' active' : ''}`
                        }
                      >
                        <span className="crm-nav-icon">{item.icon}</span>
                        <span className="crm-nav-item-label">{item.label}</span>
                        {Number(item.unread || 0) > 0 ? (
                          <span className="crm-nav-badge">{Number(item.unread || 0)}</span>
                        ) : null}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            })}
            {noSearchResults ? (
              <div className="crm-nav-empty">
                <strong>Sin resultados</strong>
                <span>No encontramos módulos para &quot;{navSearch.trim()}&quot;.</span>
              </div>
            ) : null}
          </nav>
        </div>
        <div className="crm-mobile-nav-drawer-foot">
          <div className="crm-sb-user-card">
            <button
              type="button"
              className="crm-sb-foot crm-sb-foot-btn"
              onClick={async () => {
                setIsMobileNavOpen(false);
                await handleUserClick();
              }}
              aria-label="Abrir perfil de usuario"
            >
              <div className="crm-u-ava-wrap">
                <div className="crm-u-ava">
                  {profilePhoto ? (
                    <img src={profilePhoto} alt="Avatar usuario" className="crm-u-ava-img" />
                  ) : (
                    layoutPersonDisplayInitial
                  )}
                </div>
                <span className="crm-u-status" aria-hidden="true" />
              </div>
              <div className="crm-u-meta">
                <div className="crm-u-name" title={user?.username || undefined}>{layoutPersonDisplayName}</div>
                <div className="crm-u-role">{layoutPersonRoleSubtitle}</div>
              </div>
              <IconChevronRightSmall />
            </button>
          </div>
        </div>
      </aside>
      <div
        className={`crm-profile-drawer-overlay ${isProfileDrawerOpen ? 'is-open' : ''}`}
        onClick={handleCloseProfileDrawer}
        aria-hidden={!isProfileDrawerOpen}
      />
      <ProfileDrawerShell
        className={profileDrawerClassName}
        role={isBottomNavDock ? 'dialog' : undefined}
        aria-modal={isBottomNavDock ? 'true' : undefined}
        aria-labelledby="crm-profile-drawer-title"
        aria-hidden={!isProfileDrawerOpen}
      >
        <div className="crm-profile-drawer-head">
          <div>
            <div className="crm-profile-drawer-title" id="crm-profile-drawer-title">Perfil de usuario</div>
            <div className="crm-profile-drawer-subtitle">Configuración de cuenta y seguridad</div>
            <div className="crm-profile-drawer-identity">
              <div className="crm-profile-drawer-user-pill">
                👤 {profileData.username || user?.username || 'Usuario'}
              </div>
              {profileLinkedCatalogs.length > 0 ? (
                <div className="crm-profile-drawer-link-chips" aria-label="Personas vinculadas en catálogo">
                  {profileLinkedCatalogs.map((link) => (
                    <span
                      key={link.kind}
                      className={`crm-profile-drawer-link-chip crm-profile-drawer-link-chip--${link.kind}`}
                      title={`${link.roleLabel}: ${link.name}`}
                    >
                      <span className="crm-profile-drawer-link-chip__icon" aria-hidden="true">{link.icon}</span>
                      <span className="crm-profile-drawer-link-chip__role">{link.roleLabel}</span>
                      <span className="crm-profile-drawer-link-chip__name">{link.name}</span>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="crm-profile-drawer-close"
            onClick={handleCloseProfileDrawer}
            aria-label="Cerrar panel de perfil"
          >
            ✕
          </button>
        </div>
        <div
          className={[
            'crm-profile-drawer-body',
            profileLoading ? 'is-loading-profile' : '',
            isBottomNavDock ? 'crm-profile-drawer-body--dock-bottom' : ''
          ].filter(Boolean).join(' ')}
        >
          <div
            className={
              isBottomNavDock
                ? 'crm-profile-drawer-body-scroll'
                : 'crm-profile-drawer-body-flow'
            }
          >
            {!isPasswordSectionOpen && (
              <div className="crm-profile-photo-wrap">
                <div className="crm-profile-photo-label">
                  {profilePhoto ? (
                    <button
                      type="button"
                      className="crm-profile-photo-preview-btn"
                      onClick={() => {
                        setPhotoStudioBasePhoto(profilePhotoOriginal || profilePhoto);
                        setPhotoStylePreset(savedPhotoPreset || 'natural');
                        setPhotoZoom(1);
                        setPhotoPan({ x: 0, y: 0 });
                        setIsPhotoStudioOpen(true);
                      }}
                      aria-label="Abrir Photo Studio"
                    >
                      <img
                        src={profilePhoto}
                        alt="Foto de perfil"
                        className="crm-profile-photo"
                      />
                    </button>
                  ) : (
                    <div className="crm-profile-photo-fallback">{String(profileData.username || user?.username || 'U').charAt(0).toUpperCase()}</div>
                  )}
                  <div className="crm-profile-photo-actions">
                    <label className="crm-profile-photo-edit" htmlFor="profile-photo-input">Subir foto</label>
                    <button type="button" className="crm-profile-photo-edit secondary" onClick={startCamera}>Usar cámara</button>
                  </div>
                  <div className="crm-profile-photo-hint">La foto se guarda automáticamente. No hace falta cambiar contraseña.</div>
                </div>
              </div>
            )}
            <input
              id="profile-photo-input"
              className="crm-profile-photo-input"
              type="file"
              accept="image/*"
              onChange={handleProfilePhotoChange}
            />
            {profileLoading ? (
              <ProfileDrawerLoading />
            ) : (
              <>
                {profileError ? <div className="crm-profile-error">{profileError}</div> : null}
                {!isPasswordSectionOpen && (
                  <div className="crm-profile-info-card">
                    <div className="crm-profile-info-card-head">
                      <div className="crm-profile-info-card-title">📇 Datos de contacto</div>
                      <div className="crm-profile-info-rows-inline">
                        <div className="crm-profile-info-row"><span>Usuario</span><strong>{profileData.username || user?.username || 'Usuario'}</strong></div>
                        {profileLinkedCatalogs.map((link) => (
                          <div key={`contact-${link.kind}`} className="crm-profile-info-row crm-profile-info-row--catalog">
                            <span>{link.roleLabel}</span>
                            <strong>{link.name}</strong>
                          </div>
                        ))}
                        <div className="crm-profile-info-row"><span>Email</span><strong>{profileData.correo || 'No definido'}</strong></div>
                        <div className="crm-profile-info-row"><span>Teléfono</span><strong>{profileData.telefono || 'No definido'}</strong></div>
                      </div>
                      <div className="crm-profile-inline-actions">
                        <button
                          type="button"
                          className="crm-profile-inline-btn"
                          onClick={() => copyProfileValue(profileData.correo, 'Email')}
                        >
                          Copiar email
                        </button>
                        <button
                          type="button"
                          className="crm-profile-inline-btn"
                          onClick={() => copyProfileValue(profileData.telefono, 'Teléfono')}
                        >
                          Copiar teléfono
                        </button>
                        <button
                          type="button"
                          className="crm-profile-inline-btn accent"
                          onClick={() => {
                            if (isReservedN8nProfile) {
                              Swal.fire({ toast: true, position: 'bottom-end', icon: 'warning', title: 'Usuario reservado para n8n: contraseña bloqueada.', showConfirmButton: false, timer: 2600, timerProgressBar: true, customClass: { popup: 'swal-toast-crm' } });
                              return;
                            }
                            setIsPasswordSectionOpen(true);
                          }}
                          disabled={isReservedN8nProfile}
                          title={isReservedN8nProfile ? 'Usuario reservado para n8n' : undefined}
                        >
                          Ir a contraseña
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {!isPasswordSectionOpen && (
                  <div className="crm-profile-extra-grid">
                    <button
                      type="button"
                      className="crm-profile-section-toggle"
                      onClick={() => setIsProfileExtrasOpen((v) => !v)}
                    >
                      <span>📊 Ver más detalles</span>
                      <strong aria-hidden>{isProfileExtrasOpen ? '▾' : '▸'}</strong>
                    </button>
                    <div className={`crm-profile-password-accordion ${isProfileExtrasOpen ? 'is-open' : ''}`}>
                      <div className="crm-profile-health-card">
                        <div className="crm-profile-health-head">
                          <span>Estado de cuenta</span>
                          <strong>{profileCompletionPercent}% completo</strong>
                        </div>
                        <div className="crm-profile-health-bar">
                          <span style={{ width: `${profileCompletionPercent}%` }} />
                        </div>
                        <div className="crm-profile-health-list">
                          <span className={hasEmail ? 'ok' : ''}>{hasEmail ? '✓' : '•'} Email cargado</span>
                          <span className={hasPhone ? 'ok' : ''}>{hasPhone ? '✓' : '•'} Teléfono cargado</span>
                          <span className={hasPhoto ? 'ok' : ''}>{hasPhoto ? '✓' : '•'} Foto de perfil</span>
                          <span className={hasCatalogLink ? 'ok' : ''}>
                            {hasCatalogLink ? '✓' : '•'} Vínculo en catálogo
                          </span>
                        </div>
                      </div>
                      <div className="crm-profile-quick-card">
                        <div className="crm-profile-quick-title">Resumen rápido</div>
                        <div className="crm-profile-quick-list">
                          <span><strong>Acceso:</strong> {user?.isAdmin ? 'Administrador' : 'Operativo'}</span>
                          {profileLinkedCatalogs[0] ? (
                            <span>
                              <strong>Catálogo:</strong> {profileLinkedCatalogs.map((l) => l.name).join(' · ')}
                            </span>
                          ) : null}
                          <span><strong>Sesión:</strong> Activa y protegida</span>
                          <span><strong>Cuenta:</strong> {profileCompletionPercent}% completada</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            <form id="profile-password-form" className="crm-profile-password-form" onSubmit={handleChangePassword}>
              <button
                type="button"
                className={`crm-profile-section-toggle ${isPasswordSectionOpen ? 'is-open' : ''}`}
                onClick={() => {
                  if (isReservedN8nProfile) {
                    Swal.fire({ toast: true, position: 'bottom-end', icon: 'warning', title: 'Usuario reservado para n8n: contraseña bloqueada.', showConfirmButton: false, timer: 2600, timerProgressBar: true, customClass: { popup: 'swal-toast-crm' } });
                    return;
                  }
                  setIsPasswordSectionOpen((v) => !v);
                }}
                disabled={isReservedN8nProfile}
              >
                <span>{isReservedN8nProfile ? '🔒 Contraseña bloqueada (n8n)' : '🔐 Cambiar contraseña (opcional)'}</span>
                <strong aria-hidden>{isPasswordSectionOpen ? '▾' : '▸'}</strong>
              </button>
              <div className={`crm-profile-password-accordion ${isPasswordSectionOpen ? 'is-open' : ''}`}>
                {showInlinePasswordHelper ? (
                  <div className="crm-profile-password-helper">
                    {passwordHelperMessage}
                  </div>
                ) : null}
                <div className="crm-password-block-head">
                  <span className="crm-password-block-title">Seguridad de acceso</span>
                  <span className="crm-password-block-subtitle">Completá los 3 campos para guardar una nueva contraseña.</span>
                </div>
                <div className="crm-profile-input-label">Contraseña actual</div>
                <div className={`crm-profile-input-wrap ${!canEditCurrentPassword ? 'is-disabled' : ''}`}>
                  <span className="crm-profile-input-icon" aria-hidden>🔑</span>
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    className="crm-profile-input"
                    placeholder="Contraseña actual"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    disabled={!canEditCurrentPassword}
                  />
                  <button
                    type="button"
                    className="crm-profile-eye-btn"
                    onClick={() => setShowCurrentPassword((v) => !v)}
                    aria-label={showCurrentPassword ? 'Ocultar contraseña actual' : 'Mostrar contraseña actual'}
                    disabled={!canEditCurrentPassword}
                  >
                    {showCurrentPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                <div className="crm-profile-input-label">Nueva contraseña</div>
                <div className={`crm-profile-input-wrap ${!canEditNewPassword ? 'is-disabled' : ''}`}>
                  <span className="crm-profile-input-icon" aria-hidden>🔒</span>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    className="crm-profile-input"
                    placeholder="Nueva contraseña"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={!canEditNewPassword}
                  />
                  <button
                    type="button"
                    className="crm-profile-eye-btn"
                    onClick={() => setShowNewPassword((v) => !v)}
                    aria-label={showNewPassword ? 'Ocultar nueva contraseña' : 'Mostrar nueva contraseña'}
                    disabled={!canEditNewPassword}
                  >
                    {showNewPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                <div className="crm-profile-input-label">Repetir nueva contraseña</div>
                <div className={`crm-profile-input-wrap ${!canEditConfirmPassword ? 'is-disabled' : ''}`}>
                  <span className="crm-profile-input-icon" aria-hidden>✅</span>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="crm-profile-input"
                    placeholder="Repetir nueva contraseña"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={!canEditConfirmPassword}
                  />
                  <button
                    type="button"
                    className="crm-profile-eye-btn"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    aria-label={showConfirmPassword ? 'Ocultar confirmación de contraseña' : 'Mostrar confirmación de contraseña'}
                    disabled={!canEditConfirmPassword}
                  >
                    {showConfirmPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                {hasNewPassword && (
                  <>
                    <div className="crm-password-strength-line">
                      <span>Fortaleza</span>
                      <strong className={`lvl-${passwordStrengthScore}`}>{passwordStrengthLabel}</strong>
                    </div>
                    <div className="crm-password-strength-bar">
                      <span style={{ width: `${Math.max(10, (passwordStrengthScore / 5) * 100)}%` }} />
                    </div>
                    <div className="crm-password-strength-meta">
                      <span>Score {passwordStrengthScore}/5</span>
                      <span>{passwordStrengthHint}</span>
                    </div>
                    <div className="crm-password-rules-grid">
                      <span className={passwordChecks.length ? 'ok' : ''}>8+ caracteres</span>
                      <span className={passwordChecks.upper ? 'ok' : ''}>1 mayúscula</span>
                      <span className={passwordChecks.lower ? 'ok' : ''}>1 minúscula</span>
                      <span className={passwordChecks.number ? 'ok' : ''}>1 número</span>
                      <span className={passwordChecks.symbol ? 'ok' : ''}>1 símbolo</span>
                    </div>
                    <div className="crm-password-pro-tip">
                      <strong>Tip CRM:</strong> evitá usar datos personales o secuencias obvias.
                    </div>
                  </>
                )}
                <button
                  type="button"
                  className="crm-profile-generate-btn"
                  disabled={!canEditNewPassword || changingPassword}
                  onClick={() => {
                    const generated = buildStrongPassword();
                    setNewPassword(generated);
                    setConfirmPassword(generated);
                    setPasswordChangeInlineFeedback({
                      type: 'success',
                      text: 'Se generó una contraseña segura. Revisála y guardá cuando quieras.'
                    });
                  }}
                >
                  ✨ Generar contraseña segura
                </button>
                {floatingPasswordNote ? (
                  <div className={`crm-profile-floating-note ${floatingPasswordNote.type === 'success' ? 'success' : 'error'}`}>
                    {floatingPasswordNote.text}
                  </div>
                ) : null}
              </div>
            </form>
          </div>
        </div>
        <div className="crm-profile-drawer-foot">
          {isPasswordSectionOpen ? (
            <button
              type="submit"
              form="profile-password-form"
              className="crm-profile-primary-btn"
              disabled={!canSubmitPassword}
            >
              {changingPassword ? 'Guardando...' : 'Guardar nueva contraseña'}
            </button>
          ) : <div />}
          <button type="button" className="crm-profile-logout-btn" onClick={handleDrawerLogout}>
            Cerrar sesión
          </button>
        </div>
      </ProfileDrawerShell>
      {isCameraOpen && (
        <div className="crm-camera-modal">
          <div className="crm-camera-card">
            <div className="crm-camera-head">
              <strong>Tomar foto de perfil</strong>
              <button type="button" className="crm-camera-close" onClick={stopCamera}>✕</button>
            </div>
            <video ref={videoRef} className="crm-camera-video" autoPlay playsInline muted />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            {cameraError ? <div className="crm-profile-error">{cameraError}</div> : null}
            {!isCameraReady ? <div className="crm-profile-info-line">Inicializando cámara...</div> : null}
            <div className="crm-camera-actions">
              <button type="button" className="crm-profile-primary-btn" onClick={captureFromCamera} disabled={!isCameraReady}>
                Capturar y guardar
              </button>
              <button type="button" className="crm-profile-logout-btn" onClick={stopCamera}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
      {isPhotoStudioOpen && profilePhoto && (
        <div className="crm-photo-studio-overlay">
          <div className="crm-photo-studio-card" onClick={(e) => e.stopPropagation()}>
            <div className="crm-photo-studio-head">
              <div>
                <div className="crm-photo-studio-title">Photo Studio</div>
                <div className="crm-photo-studio-subtitle">Ajustes visuales del perfil</div>
              </div>
              <button type="button" className="crm-photo-studio-close" onClick={() => setIsPhotoStudioOpen(false)}>✕</button>
            </div>
            <div className="crm-photo-studio-status-row">
              <span className="crm-photo-studio-status-pill">
                Estilo actual: <strong>{(savedPhotoPreset || 'natural').toUpperCase()}</strong>
              </span>
              <div className="crm-photo-studio-status-actions">
                <button
                  type="button"
                  className="crm-photo-studio-action-btn"
                  onMouseDown={() => setIsComparingPhoto(true)}
                  onMouseUp={() => setIsComparingPhoto(false)}
                  onMouseLeave={() => setIsComparingPhoto(false)}
                  onTouchStart={() => setIsComparingPhoto(true)}
                  onTouchEnd={() => setIsComparingPhoto(false)}
                >
                  Comparar
                </button>
                <button
                  type="button"
                  className="crm-photo-studio-action-btn danger"
                  onClick={restoreOriginalPhoto}
                  disabled={isApplyingPhotoStyle || savedPhotoPreset === 'natural'}
                >
                  Restaurar original
                </button>
              </div>
            </div>
            <div
              key={photoFlashTick}
              className="crm-photo-studio-preview-wrap flash"
              onMouseMove={handlePhotoPreviewMouseMove}
              onMouseLeave={stopPhotoDrag}
            >
              <img
                ref={photoPreviewRef}
                src={isComparingPhoto && profilePhotoOriginal ? profilePhotoOriginal : profilePhoto}
                alt="Preview perfil"
                className="crm-photo-studio-preview"
                onMouseDown={handlePhotoPreviewMouseDown}
                style={{
                  transform: `translate(${photoPan.x}px, ${photoPan.y}px) scale(${photoZoom})`,
                  cursor: photoZoom > 1 ? (isPhotoDragging ? 'grabbing' : 'grab') : 'default'
                }}
              />
            </div>
            <div className="crm-photo-zoom-row">
              <button
                type="button"
                className="crm-photo-style-btn"
                onClick={() => setPhotoZoom((z) => Math.max(1, Number((z - 0.2).toFixed(2))))}
              >
                Zoom -
              </button>
              <span className="crm-photo-zoom-label">{Math.round(photoZoom * 100)}%</span>
              <button
                type="button"
                className="crm-photo-style-btn"
                onClick={() => setPhotoZoom((z) => Math.min(2.6, Number((z + 0.2).toFixed(2))))}
              >
                Zoom +
              </button>
              <button
                type="button"
                className="crm-photo-style-btn"
                onClick={() => {
                  setPhotoZoom(1);
                  setPhotoPan({ x: 0, y: 0 });
                }}
              >
                Reset vista
              </button>
            </div>
            <div className="crm-photo-style-row">
              <button type="button" className={`crm-photo-style-btn ${photoStylePreset === 'natural' ? 'active' : ''}`} onClick={() => applyPhotoPreset('natural')} disabled={isApplyingPhotoStyle}>Natural</button>
              <button type="button" className={`crm-photo-style-btn ${photoStylePreset === 'cool' ? 'active' : ''}`} onClick={() => applyPhotoPreset('cool')} disabled={isApplyingPhotoStyle}>Cool</button>
              <button type="button" className={`crm-photo-style-btn ${photoStylePreset === 'bw' ? 'active' : ''}`} onClick={() => applyPhotoPreset('bw')} disabled={isApplyingPhotoStyle}>B&W</button>
              <button type="button" className={`crm-photo-style-btn ${photoStylePreset === 'warm' ? 'active' : ''}`} onClick={() => applyPhotoPreset('warm')} disabled={isApplyingPhotoStyle}>Warm</button>
            </div>
            <div className="crm-photo-studio-footer">
              <button
                type="button"
                className="crm-photo-studio-save-btn"
                onClick={handleSaveCurrentPhoto}
                disabled={isApplyingPhotoStyle}
              >
                {isApplyingPhotoStyle ? 'Guardando...' : 'Guardar cambios'}
              </button>
              <button
                type="button"
                className="crm-photo-studio-close-btn"
                onClick={() => setIsPhotoStudioOpen(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
      <main className="crm-main" ref={mainRef}>
        <Outlet />
      </main>
      <ArgentinaMundialWelcome
        open={mundialWelcome.open}
        onClose={() => {
          consumeMundialWelcomePending();
          mundialWelcome.close();
        }}
        displayName={mundialWelcome.displayName}
      />
      <ChatwootAssignmentToastListener />
      <PulseCommandPalette
        isOpen={pulseCommand.isOpen}
        query={pulseCommand.query}
        onQueryChange={pulseCommand.setQuery}
        groups={pulseCommand.groups}
        flatItems={pulseCommand.flatItems}
        activeIndex={pulseCommand.activeIndex}
        onActiveIndexChange={pulseCommand.setActiveIndex}
        loading={pulseCommand.loading}
        onClose={pulseCommand.close}
        onRun={pulseCommand.runItem}
        onKeyDown={pulseCommand.handlePaletteKeyDown}
      />
      <PulseCommandDock isOpen={pulseCommand.isOpen} onOpen={pulseCommand.open} />
      <PulseAboutDock
        isOpen={isAboutPanelOpen}
        onOpen={() => setIsAboutPanelOpen(true)}
        onClose={() => setIsAboutPanelOpen(false)}
      />
      <ChatwootIncomingMessageFab
        enabled={canSeeConversaciones}
        unreadCount={unreadCount}
        items={unreadFabItems}
      />
      {isBottomNavDock ? (
        <CrmBottomDockLauncherPortal
          isOpen={isBottomDockLauncherOpen}
          navGroups={visibleNavGroups}
          pinnedPaths={effectiveLeftDockPins}
          pinnedCount={allDockPinnedCount}
          totalCount={allDockableNavCount}
          activePath={location.pathname}
          searchQuery={navSearch}
          onSearchQueryChange={setNavSearch}
          onClose={() => setIsBottomDockLauncherOpen(false)}
          onTogglePin={toggleLeftDockPin}
          draggingPath={navDockDragPin.draggingPath}
          onItemDragStart={navDockDragPin.handleItemDragStart}
          onItemDragEnd={navDockDragPin.handleItemDragEnd}
          maxPins={MAX_BOTTOM_DOCK_PINS}
          dockLimitFlash={dockLimitFlash}
        />
      ) : null}
      {crmNavShortcuts.length > 0 ? (
        <>
          <CrmShortcutsDock
            isOpen={isShortcutsPanelOpen}
            shortcuts={crmNavShortcuts}
            onOpen={openShortcutsPanel}
            onClose={closeShortcutsPanel}
            onSelect={goToCrmShortcut}
          />
          <CrmShortcutsToast flash={shortcutsFlash} />
        </>
      ) : null}
      {/* Kiosk button moved to sidebar brand area for better UX */}
      {genieFx && typeof document !== 'undefined'
        ? createPortal(
          <>
            <div
              key={`genie-fx-${genieFx.id}`}
              className="pulse-genie-fx"
              style={{ '--gx': `${genieFx.x}px`, '--gy': `${genieFx.y}px` }}
              aria-hidden="true"
            >
              <span className="pulse-genie-fx__glow" />
              <span className="pulse-genie-fx__column" />
            </div>
            <div key={`genie-sheen-${genieFx.id}`} className="pulse-genie-sheen" aria-hidden="true">
              <i />
            </div>
          </>,
          document.body
        )
        : null}
    </div>
  );
}

export default PrivateLayout;
